import { motion, AnimatePresence } from 'motion/react';
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { BookOpen, Sparkles } from 'lucide-react';
import { TTSButton } from './components/TTSButton';
import { GameState, Difficulty, WordItem, PlayerProfile } from './types';
import {
  playSound,
  playBGM,
  getAudioState,
  subscribeAudioState,
  unlockAudioContext
} from './audio';
import {
  LEVEL_DATA,
  ALL_WORDS_DEBUTANT,
  ALL_WORDS_CONFIRME,
  DICTIONARY_BY_CATEGORY,
  SORTED_CATEGORIES,
  INTRO_DIALOGUES,
  REGION_INTRO_DIALOGUES,
  BOSS_INTRO_DIALOGUES,
  EPILOGUE_DIALOGUES,
  PATH_NODES
} from './data/gameData';
import { WeatherCanvas } from './components/WeatherCanvas';
import { StrangeFog } from './components/StrangeFog';
import { MainTitleScreenArtwork } from './components/MainTitleScreenArtwork';
import { TitleScreenArtwork, TitleScreenBackground } from './components/TitleScreenArtwork';
import { NpcSprite, PlayerSprite, BerberRugBorder, BerberRugDivider } from './components/Sprites';
import { WorldMapArtwork, TexturedLevelArtwork, LevelEntrancePorticoOverlay } from './components/Artwork';
import { LevelSurroundingArtwork } from './components/LevelSurroundingArtwork';
import { LevelGroundDamier } from './components/LevelGroundDamier';
import { IntroCutsceneArtwork } from './components/IntroCutsceneArtwork';
import { SteleAltarMonument } from './components/SteleAltarMonument';
import { BrokenCauldronToxicProps } from './components/BrokenCauldronToxicProps';
import { MusicToggleButton } from './components/MusicToggleButton';
import { ScrollDualScreenReader } from './components/ScrollDualScreenReader';
import { CreatorWelcomeModal } from './components/CreatorWelcomeModal';
import { TrainingGuideModal } from './components/TrainingGuideModal';
import { LegalRightsModal } from './components/LegalRightsModal';
import { getWordIcon } from './utils/emojis';
import { formatPromptWithGrammar, stripGrammarParentheses } from './utils/kabyleGrammar';
import {
  getActivePlayerId,
  getActivePlayerProfile,
  setActivePlayerId,
  getOrCreatePlayerProfile,
  autoSavePlayerData,
  saveLevelProgress,
  getAllProfiles,
  ADMIN_PLAYER_ID,
  normalizePlayerId,
  hasPlayerSaveData,
  findExistingProfileByName
} from './utils/saveManager';
import { DictionaryView } from './components/DictionaryView';
import { InventoryView } from './components/InventoryView';
import { ProgressionView } from './components/ProgressionView';
import { ChallengesView } from './components/ChallengesView';
import { CelestialMoonChallenge } from './components/CelestialMoonChallenge';
import { AdminDashboardView } from './components/AdminDashboardView';
import { AdminPasswordModal } from './components/AdminPasswordModal';
import { LEVEL_THRESHOLDS, TierEmblem } from './components/TierBadge';

export default function App() {
  const [gameState, setGameStateInternal] = useState<GameState>('TITLE');
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [showWhiteFlash, setShowWhiteFlash] = useState(false);
  const isTransitioningRef = useRef(false);

  const setGameState = useCallback((newState: GameState) => {
    if (newState === gameState || isTransitioningRef.current) return;
    
    // Only show loading screen when entering a level (or challenge)
    const isStartingLevel = (newState === 'REGION_INTRO' || newState === 'CELESTIAL_CHALLENGE' || newState === 'INTRO');

    if (!isStartingLevel) {
      setGameStateInternal(newState);
      return;
    }

    isTransitioningRef.current = true;
    setIsTransitioning(true);
    
    setTimeout(() => {
      setGameStateInternal(newState);
    }, 1000);

    setTimeout(() => {
      setIsTransitioning(false);
      isTransitioningRef.current = false;
    }, 2000);
  }, [gameState]);

  // Initialize active player profile directly from saveManager
  const initialProfile = getActivePlayerProfile();

  const [playerId, setPlayerId] = useState<string>(() => initialProfile.id);
  const [playerName, setPlayerName] = useState<string>(() => {
    let clean = (initialProfile.name || '').trim();
    while (clean.startsWith('player_')) {
      clean = clean.slice(7);
    }
    return clean === 'Aqcic' || !clean ? 'Voyageur' : clean;
  });
  const [difficulty, setDifficulty] = useState<Difficulty>(() => initialProfile.difficulty || 'DEBUTANT');
  const [tempName, setTempName] = useState('');
  
  const [unlockedLevels, setUnlockedLevels] = useState<number[]>(() => initialProfile.unlockedLevels);
  const [score, setScore] = useState<number>(() => initialProfile.score);
  const [unlockedSkins, setUnlockedSkins] = useState<string[]>(() => initialProfile.unlockedSkins);
  const [currentSkin, setCurrentSkin] = useState<string>(() => initialProfile.currentSkin);

  const [currentLevelIdx, setCurrentLevelIdx] = useState<number>(0);
  const [selectedMapNode, setSelectedMapNode] = useState<number>(0);

  const [introStep, setIntroStep] = useState<number>(0);
  const [regionIntroStep, setRegionIntroStep] = useState<number>(0);
  const [bossIntroStep, setBossIntroStep] = useState<number>(0);
  const [victoryStep, setVictoryStep] = useState<number>(0);
  const [epilogueStep, setEpilogueStep] = useState<number>(0);
  const [npcDialogueStep, setNpcDialogueStep] = useState<number>(0);

  const [saveAnnouncement, setSaveAnnouncement] = useState<{
    show: boolean;
    levelName: string;
    levelNumber: number;
  } | null>(null);

  const [step, setStep] = useState<number>(0);
  const [subStep, setSubStep] = useState<number>(0);
  const [hearts, setHearts] = useState<number>(5);
  const [maxHearts, setMaxHearts] = useState<number>(5);

  const seenWordsRef = useRef<string[]>(initialProfile.seenWords || []);

  // Sync profile metadata (skin, name, difficulty) when modified outside gameplay
  const saveProfilePreferences = (overrides?: Partial<PlayerProfile>) => {
    autoSavePlayerData(playerId, {
      name: playerName,
      unlockedLevels,
      score,
      unlockedSkins,
      currentSkin,
      seenWords: seenWordsRef.current,
      difficulty,
      ...overrides
    });
  };
  
  const [inventoryInitialTab, setInventoryInitialTab] = useState<'skins' | 'scrolls' | 'sms'>('skins');
  const [cauldronLiberated, setCauldronLiberated] = useState<boolean>(false);

  const openInventory = (tab?: 'skins' | 'scrolls' | 'sms') => {
    clickSFX();
    const safeTab: 'skins' | 'scrolls' | 'sms' = tab === 'scrolls' ? 'scrolls' : tab === 'sms' ? 'sms' : 'skins';
    setInventoryInitialTab(safeTab);
    previousStateRef.current = gameState;
    setGameState('INVENTORY');
  };

  const openProgression = () => {
    clickSFX();
    previousStateRef.current = gameState;
    setGameState('PROGRESSION');
  };

  const openChallenges = () => {
    clickSFX();
    previousStateRef.current = gameState;
    setGameState('CHALLENGES');
  };
  
  const closeInventory = () => {
    clickSFX();
    saveProfilePreferences({ currentSkin, unlockedSkins, score });
    setGameState(previousStateRef.current === 'INVENTORY' ? 'MAIN_MENU' : previousStateRef.current);
  };

  const [combo, setCombo] = useState<number>(0);

  const [challengeVictory, setChallengeVictory] = useState<{
    themeName: string;
    npcName: string;
    heartGained: boolean;
    xpGained: number;
    nextStep: number;
  } | null>(null);

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const triggerToast = (msg: string) => {
    if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
    setToastMessage(msg);
    toastTimeoutRef.current = setTimeout(() => {
      setToastMessage(null);
    }, 1300);
  };

  const [readingScroll, setReadingScroll] = useState<{
    title: string;
    content: string;
  } | null>(null);

  const failedCurrentRunRef = useRef<WordItem[]>([]);
  const askedInCurrentStepRef = useRef<string[]>([]);
  const lastAskedRef = useRef<string | null>(null);
  const [failedCount, setFailedCount] = useState<number>(0);

  const [isWalking, setIsWalking] = useState<boolean>(false);
  const [isJumping, setIsJumping] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<'CORRECT' | 'WRONG' | null>(null);

  const [currentQuestion, setCurrentQuestion] = useState<WordItem | null>(null);
  const [options, setOptions] = useState<WordItem[]>([]);
  const [failedOptionKabs, setFailedOptionKabs] = useState<string[]>([]);
  const [displayedText, setDisplayedText] = useState<string>('');
  const currentFullMessageRef = useRef<string>('');
  const isTypingRef = useRef<boolean>(false);
  const typingIntervalRef = useRef<number | null>(null);
  const [currentSpeaker, setCurrentSpeaker] = useState<{ speaker: string; icon: string }>({
    speaker: 'Amusnaw (Le Sage)',
    icon: '🌫️'
  });
  const dialogueBoxRef = useRef<HTMLDivElement | null>(null);

  // Responsive game container width observation for automatic decor spacing
  const gameContainerRef = useRef<HTMLDivElement | null>(null);
  const [viewportWidth, setViewportWidth] = useState<number>(() => {
    return typeof window !== 'undefined' ? window.innerWidth : 480;
  });

  useEffect(() => {
    let rafId: number | null = null;
    let lastReportedW = 0;

    const scheduleWidthUpdate = (rawW: number) => {
      const roundedW = Math.round(rawW);
      if (Math.abs(roundedW - lastReportedW) < 4) return;
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        lastReportedW = roundedW;
        setViewportWidth(roundedW);
      });
    };

    const updateWidth = () => {
      if (gameContainerRef.current) {
        const w = gameContainerRef.current.clientWidth;
        if (w > 0) scheduleWidthUpdate(w);
      } else if (typeof window !== 'undefined') {
        scheduleWidthUpdate(window.innerWidth);
      }
    };
    updateWidth();

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const w = entry.contentRect?.width || entry.contentBoxSize?.[0]?.inlineSize;
        if (w && w > 0) {
          scheduleWidthUpdate(w);
        }
      }
    });

    if (gameContainerRef.current) {
      resizeObserver.observe(gameContainerRef.current);
    }
    window.addEventListener('resize', updateWidth, { passive: true });

    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateWidth);
    };
  }, []);

  // Auto-scroll the dialogue container smoothly when text expands
  useEffect(() => {
    if (dialogueBoxRef.current) {
      const box = dialogueBoxRef.current;
      const frame = requestAnimationFrame(() => {
        if (box) {
          box.scrollTop = box.scrollHeight;
        }
      });
      return () => cancelAnimationFrame(frame);
    }
  }, [displayedText]);

  const [audioSettings, setAudioSettings] = useState(() => getAudioState());

  useEffect(() => {
    const unsubscribe = subscribeAudioState(() => {
      setAudioSettings(getAudioState());
    });
    return unsubscribe;
  }, []);

  // Update BGM & Ambience whenever gameState changes
  useEffect(() => {
    if (gameState === 'INTRO' || gameState === 'EPILOGUE' ||
      gameState === 'EPILOGUE_ANIM') {
      playBGM('prologue');
    } else if (
      gameState === 'WORLD_MAP' ||
      gameState === 'MAIN_MENU' ||
      gameState === 'TITLE' ||
      gameState === 'ADVENTURE_TITLE' ||
      gameState === 'NAME_INPUT' ||
      gameState === 'DICTIONARY' ||
      gameState === 'INVENTORY' ||
      gameState === 'CHALLENGES' ||
      gameState === 'ADMIN_DASHBOARD'
    ) {
      playBGM('world_map');
    } else if (
      gameState === 'RUN' ||
      gameState === 'REVISION' ||
      gameState === 'REVISION_PLAY' ||
      gameState === 'REGION_INTRO' ||
      gameState === 'NPC_DIALOGUE'
    ) {
      playBGM('in_game');
    } else if (gameState === 'BOSS_INTRO' || gameState === 'BOSS') {
      playBGM('boss');
    } else if (gameState === 'VICTORY') {
      playBGM('victory');
    } else if (gameState === 'GAME_OVER') {
      playBGM('none');
    }
  }, [gameState]);

  // Unlock Web Audio on first user interaction anywhere
  useEffect(() => {
    const unlockAudio = () => {
      unlockAudioContext();
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
      window.removeEventListener('touchstart', unlockAudio);
    };
    window.addEventListener('pointerdown', unlockAudio, { once: true });
    window.addEventListener('keydown', unlockAudio, { once: true });
    window.addEventListener('touchstart', unlockAudio, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlockAudio);
      window.removeEventListener('keydown', unlockAudio);
      window.removeEventListener('touchstart', unlockAudio);
    };
  }, []);

  const previousStateRef = useRef<GameState>('TITLE');

  const openDictionary = () => {
    clickSFX();
    previousStateRef.current = gameState;
    setGameState('DICTIONARY');
  };

  const closeDictionary = () => {
    clickSFX();
    setGameState(previousStateRef.current === 'DICTIONARY' ? 'MAIN_MENU' : previousStateRef.current);
  };

  const clickSFX = () => playSound('click');

  const getDialogueDisplay = (rawText: string, defaultSpeaker = 'Narration', defaultIcon = '📜') => {
    if (!rawText) return { speaker: defaultSpeaker, icon: defaultIcon, text: '' };
    
    let line = rawText;
    if (playerName) {
      line = line.replace(/a mmi/g, playerName);
      line = line.replace(/Azul !/g, `Azul ${playerName} !`);
    }

    if (/^Amusnaw\s*:/i.test(line)) {
      return {
        speaker: 'Amusnaw (Le Sage)',
        icon: '🌫️',
        text: line.replace(/^Amusnaw\s*:\s*/i, '').trim()
      };
    }

    if (/^Pensée\s*(?:de\s*[^:]+|du\s*joueur)?\s*:/i.test(line)) {
      return {
        speaker: `Pensée de ${playerName || 'a mmi'}`,
        icon: '💭',
        text: line.replace(/^Pensée\s*(?:de\s*[^:]+|du\s*joueur)?\s*:\s*/i, '').trim()
      };
    }

    if (/^Jedjiga\s*:/i.test(line)) {
      return {
        speaker: 'Jedjiga (La Sorcière)',
        icon: '🔮',
        text: line.replace(/^Jedjiga\s*:\s*/i, '').trim()
      };
    }

    if (/^(?:Dda Sliman|Fermier)\s*:/i.test(line)) {
      return {
        speaker: 'Dda Sliman (Le Fermier)',
        icon: '🌾',
        text: line.replace(/^(?:Dda Sliman|Fermier)\s*:\s*/i, '').trim()
      };
    }

    if (/^(?:Aɛekkaf|Ermite)\s*:/i.test(line)) {
      return {
        speaker: 'Aɛekkaf (L\'Ermite)',
        icon: '⛰️',
        text: line.replace(/^(?:Aɛekkaf|Ermite)\s*:\s*/i, '').trim()
      };
    }

    if (/^(?:Tamedyazt|Poétesse)\s*:/i.test(line)) {
      return {
        speaker: 'Tamedyazt (La Poétesse)',
        icon: '🪕',
        text: line.replace(/^(?:Tamedyazt|Poétesse)\s*:\s*/i, '').trim()
      };
    }

    if (/^(?:Na\s*Faḍma|Nna\s*Faḍma|Faḍma|Fadhma)\s*:/i.test(line)) {
      return {
        speaker: 'Na Faḍma (L\'Ancienne)',
        icon: '👵',
        text: line.replace(/^(?:Na\s*Faḍma|Nna\s*Faḍma|Faḍma|Fadhma)\s*:\s*/i, '').trim()
      };
    }

    if (/^(?:Ameksa|Berger)\s*:/i.test(line)) {
      return {
        speaker: 'Ameksa (Le Berger)',
        icon: '🐑',
        text: line.replace(/^(?:Ameksa|Berger)\s*:\s*/i, '').trim()
      };
    }

    const customSpeaker = line.match(/^([^:«»\n]{2,30})\s*:\s*(.+)$/s);
    if (customSpeaker) {
      return {
        speaker: customSpeaker[1].trim(),
        icon: defaultIcon || '💬',
        text: customSpeaker[2].trim()
      };
    }

    return {
      speaker: defaultSpeaker,
      icon: defaultIcon,
      text: line.trim()
    };
  };

  useEffect(() => {
    let fullText = '';
    let defSpeaker = 'Narration';
    let defIcon = '📜';

    if (gameState === 'INTRO') {
      fullText = INTRO_DIALOGUES[introStep] || '';
      defSpeaker = 'Amusnaw (Le Sage)';
      defIcon = '🌫️';
    } else if (gameState === 'EPILOGUE') {
      fullText = EPILOGUE_DIALOGUES[epilogueStep] || '';
      defSpeaker = 'Amusnaw (Le Sage)';
      defIcon = '🏆';
    } else if (gameState === 'REGION_INTRO') {
      const regionDialogues = REGION_INTRO_DIALOGUES[currentLevelIdx] || [LEVEL_DATA[currentLevelIdx].regionIntro];
      fullText = regionDialogues[regionIntroStep] || regionDialogues[0] || '';
      defSpeaker = LEVEL_DATA[currentLevelIdx].name;
      defIcon = '🌫️';
    } else if (gameState === 'BOSS_INTRO') {
      const bossDialogues = BOSS_INTRO_DIALOGUES[currentLevelIdx] || [LEVEL_DATA[currentLevelIdx].bossIntro];
      fullText = bossDialogues[bossIntroStep] || bossDialogues[0] || '';
      defSpeaker = `Gardien de la Stèle : ${LEVEL_DATA[currentLevelIdx].bossWord.fr}`;
      defIcon = LEVEL_DATA[currentLevelIdx].bossIcon || '⚔️';
    } else if (gameState === 'NPC_DIALOGUE') {
      const lvlData = LEVEL_DATA[currentLevelIdx];
      if (lvlData.npcEncounter && step === lvlData.npcEncounter.step) {
        fullText = lvlData.npcEncounter.dialogues[npcDialogueStep] || '';
        defSpeaker = lvlData.npcEncounter.name || 'Villageois';
        defIcon = '💬';
      } else if (lvlData.specialNodes && lvlData.specialNodes[step]) {
        const sp = lvlData.specialNodes[step];
        const list = sp.npc.dialogues || (sp.npc.dialogue ? [sp.npc.dialogue] : []);
        fullText = list[npcDialogueStep] || list[0] || '';
        defSpeaker = sp.npc.name || 'Villageois';
        defIcon = '🌟';
      }
    } else if (gameState === 'REVISION') {
      fullText = "Gardien : Halte ! Tu as fait des erreurs en chemin. Répare-les avant de m'affronter.";
      defSpeaker = 'Gardien de la Stèle';
      defIcon = '⚔️';
    } else if (gameState === 'VICTORY') {
      const remainingSteles = Math.max(0, 5 - (currentLevelIdx + 1));
      const scrollTitle = LEVEL_DATA[currentLevelIdx].scroll?.title || '';
      const vDialogues = [
        `Amusnaw : La stèle sacrée est libérée ! Votre progression a été enregistrée avec succès. Le brouillard se dissipe sur ${LEVEL_DATA[currentLevelIdx].name} et la lumière renaît.`,
        `Amusnaw : Les défis et mots remportés dans ce niveau sont désormais disponibles dans ton Inventaire si tu souhaites t'entraîner et te perfectionner !`,
        remainingSteles > 0
          ? `Amusnaw : Mais la brume étouffe encore ${remainingSteles} région${remainingSteles > 1 ? 's' : ''} : d'autres stèles attendent d'être sauvées !`
          : 'Amusnaw : Toutes les terres kabyles sont désormais libérées et la langue ancestrale est sauvée !'
      ];
      if (scrollTitle) {
        vDialogues.push(`Amusnaw : 📜 Nouveau Conte débloqué : « ${scrollTitle} » ! Lis-le dans ton inventaire.`);
      }
      fullText = vDialogues[victoryStep] || vDialogues[0] || '';
      defSpeaker = 'Amusnaw (Le Sage)';
      defIcon = '🏆';
    }

    if (fullText && playerName) {
      fullText = fullText.replace(/a mmi/g, playerName);
      fullText = fullText.replace(/Azul !/g, `Azul ${playerName} !`);
    }

    if (fullText) {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
        typingIntervalRef.current = null;
      }

      const parsed = getDialogueDisplay(fullText, defSpeaker, defIcon);
      setCurrentSpeaker({ speaker: parsed.speaker, icon: parsed.icon });
      const messageToType = parsed.text;
      currentFullMessageRef.current = messageToType;
      isTypingRef.current = true;
      
      let i = 0;
      setDisplayedText('');
      const stepChars = messageToType.length > 80 ? 3 : 2;
      const interval = window.setInterval(() => {
        i = Math.min(i + stepChars, messageToType.length);
        setDisplayedText(messageToType.slice(0, i));
        const currentChar = messageToType[i - 1];
        if (i % 6 === 0 && currentChar && currentChar.trim() !== '') {
          playSound('dialogue');
        }
        if (i >= messageToType.length) {
          isTypingRef.current = false;
          if (typingIntervalRef.current) {
            clearInterval(typingIntervalRef.current);
            typingIntervalRef.current = null;
          }
        }
      }, 30);
      typingIntervalRef.current = interval;
      return () => {
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
        }
        isTypingRef.current = false;
      };
    }
  }, [gameState, introStep, epilogueStep, regionIntroStep, bossIntroStep, victoryStep, npcDialogueStep, currentLevelIdx, step, playerName]);

  const handleNodeSelect = useCallback((id: number) => {
    clickSFX();
    setSelectedMapNode(id);
  }, []);

  const hasActiveSave = hasPlayerSaveData({
    id: playerId,
    name: playerName,
    score,
    unlockedLevels,
    unlockedSkins,
    currentSkin,
    seenWords: seenWordsRef.current,
    lastSaved: Date.now(),
    createdAt: Date.now(),
    difficulty,
    isAdmin: playerName === 'Admin'
  });

  const startAdventure = () => {
    clickSFX();
    setDifficulty('DEBUTANT');
    saveProfilePreferences({ difficulty: 'DEBUTANT' });

    // If player already has save data, do not replay the prologue, go straight to world map
    if (hasActiveSave) {
      setGameState('WORLD_MAP');
    } else {
      setIntroStep(0);
      setGameState('INTRO');
    }
  };

  const selectDifficulty = (diff: Difficulty) => {
    clickSFX();
    setDifficulty(diff);
    saveProfilePreferences({ difficulty: diff });

    // If player already has save data, do not replay the prologue, go straight to world map
    if (hasActiveSave) {
      setGameState('WORLD_MAP');
    } else {
      setIntroStep(0);
      setGameState('INTRO');
    }
  };

  const changeDifficulty = (diff: Difficulty) => {
    clickSFX();
    setDifficulty(diff);
    saveProfilePreferences({ difficulty: diff });
    triggerToast(
      diff === 'CONFIRME'
        ? '⚔️ Mode Confirmé activé (Phrases • 3 ❤️)'
        : '🌱 Mode Débutant activé (Mots simples • 5 ❤️)'
    );
  };

  const skipIntro = () => {
    clickSFX();
    setGameState('WORLD_MAP');
  };

  const switchPlayerProfile = (nameOrId: string) => {
    clickSFX();
    const existing = findExistingProfileByName(nameOrId);
    const profile = existing || getOrCreatePlayerProfile(nameOrId);
    setPlayerId(profile.id);
    setPlayerName(profile.name);
    setUnlockedLevels(profile.unlockedLevels);
    setScore(profile.score);
    setUnlockedSkins(profile.unlockedSkins);
    setCurrentSkin(profile.currentSkin);
    if (profile.difficulty) {
      setDifficulty(profile.difficulty);
    }
    seenWordsRef.current = profile.seenWords || [];
    setActivePlayerId(profile.id);
    setGameState('MAIN_MENU');
  };

  const submitName = () => {
    clickSFX();
    const inputClean = tempName.trim();
    const finalName = inputClean || (playerName && playerName !== 'Voyageur' ? playerName : 'Voyageur');
    
    // Si l'utilisateur tape "Admin", demander le mot de passe de sécurité
    if (finalName.trim().toLowerCase() === 'admin' || finalName.trim() === ADMIN_PLAYER_ID) {
      setShowAdminPasswordModal(true);
      return;
    }

    switchPlayerProfile(finalName);
    const hasSeenFirstRun = (() => {
      try {
        return localStorage.getItem('tamazight_first_run_tutorial_seen') === 'true';
      } catch {
        return false;
      }
    })();
    if (!hasSeenFirstRun) {
      setIsFirstLaunchFlow(true);
      setShowCreatorWelcomeModal(true);
    }
  };

  const isDialogueTyping = () => {
    return isTypingRef.current || displayedText.length < currentFullMessageRef.current.length;
  };

  const revealFullDialogue = () => {
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
    isTypingRef.current = false;
    setDisplayedText(currentFullMessageRef.current);
  };

  const nextIntro = () => {
    clickSFX();
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
    isTypingRef.current = false;

    if (introStep < INTRO_DIALOGUES.length - 1) {
      setIntroStep(introStep + 1);
    } else {
      setGameState('WORLD_MAP');
    }
  };

  const nextRegionIntro = () => {
    clickSFX();
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
    isTypingRef.current = false;

    const currentRegionDialogues = REGION_INTRO_DIALOGUES[currentLevelIdx] || [LEVEL_DATA[currentLevelIdx].regionIntro];
    if (regionIntroStep < currentRegionDialogues.length - 1) {
      setRegionIntroStep(regionIntroStep + 1);
    } else {
      if (currentLevelData.npcEncounter && currentLevelData.npcEncounter.step === 0) {
        setGameState('NPC_DIALOGUE');
        setNpcDialogueStep(0);
      } else {
        setGameState('RUN');
        generateQuestion();
      }
    }
  };

  const nextBossIntro = () => {
    clickSFX();
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
    isTypingRef.current = false;

    const currentBossDialogues = BOSS_INTRO_DIALOGUES[currentLevelIdx] || [LEVEL_DATA[currentLevelIdx].bossIntro];
    if (bossIntroStep < currentBossDialogues.length - 1) {
      setBossIntroStep(bossIntroStep + 1);
    } else {
      setGameState('BOSS');
      generateQuestion(true, currentLevelIdx, 10);
    }
  };

  const nextEpilogue = () => {
    clickSFX();
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
    isTypingRef.current = false;

    if (epilogueStep < EPILOGUE_DIALOGUES.length - 1) {
      const nextStep = epilogueStep + 1;
      setEpilogueStep(nextStep);
      if (nextStep === 6) {
        playSound('boss');
      }
    } else {
      setVictoryStep(0);
      setGameState('VICTORY');
    }
  };

  const nextVictory = () => {
    clickSFX();
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
    isTypingRef.current = false;

    const scrollTitle = LEVEL_DATA[currentLevelIdx].scroll?.title || '';
    const vCount = 3 + (scrollTitle ? 1 : 0);
    if (victoryStep < vCount - 1) {
      setVictoryStep(victoryStep + 1);
    } else {
      if (currentLevelIdx === 4 && epilogueStep === 0) {
        setGameState('EPILOGUE');
      } else {
        setGameState('WORLD_MAP');
      }
    }
  };

  const generateQuestion = useCallback(
    (
      isBoss = false,
      targetLevelIdx = currentLevelIdx,
      targetStep = step,
      isRev = false,
      failedList = failedCurrentRunRef.current,
      currentSubStep = subStep
    ) => {
      const lvlData = LEVEL_DATA[targetLevelIdx];
      const currentVocabList =
        difficulty === 'CONFIRME' ? lvlData.vocabConfirme : lvlData.vocabDebutant;
      const allWordsPool =
        difficulty === 'CONFIRME' ? ALL_WORDS_CONFIRME : ALL_WORDS_DEBUTANT;

      let targetWord: WordItem;

      if (currentSubStep === 0 && !isRev && !isBoss) {
        askedInCurrentStepRef.current = [];
      }

      if (isBoss) {
        targetWord = lvlData.bossWord;
      } else if (isRev && failedList.length > 0) {
        targetWord = failedList[0];
      } else {
        const themes = lvlData.specialNodes || {};
        let availableWords: WordItem[] = [];

        if (themes[targetStep]) {
          const themeKeyword = themes[targetStep].keyword;
          const isMatchingTheme = (wType: string) => {
            if (wType === themeKeyword) return true;
            if (themeKeyword === 'Phrases & Quotidien') {
              return ['Phrases & Quotidien', 'Questions & Réponses', 'Salutations & Politesse', 'Pronoms & Personnes'].includes(wType);
            }
            if (themeKeyword === 'Verbes' || themeKeyword === "Verbes d'Action" || themeKeyword === 'Actions & Verbes') {
              return ['Verbes', "Verbes d'Action", 'Verbes au Passé', 'Verbes au Futur & Présent', 'Pensée & Communication'].includes(wType);
            }
            return false;
          };

          let dictWords = DICTIONARY_BY_CATEGORY[themeKeyword] || [];
          if (themeKeyword === 'Phrases & Quotidien') {
            dictWords = [
              ...(DICTIONARY_BY_CATEGORY['Phrases & Quotidien'] || []),
              ...(DICTIONARY_BY_CATEGORY['Questions & Réponses'] || []),
              ...(DICTIONARY_BY_CATEGORY['Salutations & Politesse'] || []),
              ...(DICTIONARY_BY_CATEGORY['Pronoms & Personnes'] || []),
            ];
          } else if (themeKeyword === 'Verbes' || themeKeyword === "Verbes d'Action") {
            dictWords = [
              ...(DICTIONARY_BY_CATEGORY['Verbes'] || []),
              ...(DICTIONARY_BY_CATEGORY["Verbes d'Action"] || []),
              ...(DICTIONARY_BY_CATEGORY['Verbes au Passé'] || []),
              ...(DICTIONARY_BY_CATEGORY['Verbes au Futur & Présent'] || []),
              ...(DICTIONARY_BY_CATEGORY['Pensée & Communication'] || []),
            ];
          }
          
          availableWords = allWordsPool.filter((w) => isMatchingTheme(w.type));
          
          // If less than 10 words, merge with the other difficulty's words from dictWords
          if (availableWords.length < 10) {
             const extra = dictWords.filter(w => !availableWords.find(a => a.kab === w.kab));
             availableWords = [...availableWords, ...extra];
          }

          if (availableWords.length === 0) availableWords = currentVocabList;
        } else {
          availableWords = currentVocabList;
        }

        // Never repeat a word in the exact same challenge step
        let challengeUnseen = availableWords.filter((w) => !askedInCurrentStepRef.current.includes(w.kab));
        if (challengeUnseen.length === 0) {
          challengeUnseen = availableWords; // fallback if we somehow asked everything
        }

        let unseen = challengeUnseen.filter((w) => !seenWordsRef.current.includes(w.kab));
        if (unseen.length > 1) {
          unseen = unseen.filter((w) => w.kab !== lastAskedRef.current);
        }

        if (unseen.length > 0) {
          targetWord = unseen[Math.floor(Math.random() * unseen.length)];
        } else {
          let seenPool = challengeUnseen;
          if (seenPool.length > 1) {
            seenPool = seenPool.filter((w) => w.kab !== lastAskedRef.current);
          }
          targetWord = seenPool[Math.floor(Math.random() * seenPool.length)];
        }
      }

      askedInCurrentStepRef.current.push(targetWord.kab);
      lastAskedRef.current = targetWord.kab;

      const pool = allWordsPool.filter(
        (w) => w.kab !== targetWord.kab && w.kab !== lvlData.bossWord.kab
      );
      const uniqueWrongOptions: WordItem[] = [];
      const usedFr = new Set<string>([targetWord.fr]);
      const usedKab = new Set<string>([targetWord.kab]);

      let priorityPool = pool;
      if (difficulty === 'CONFIRME') {
        priorityPool = pool.filter(w => w.type === targetWord.type);
      }
      
      const shuffledPriorityPool = [...priorityPool].sort(() => Math.random() - 0.5);

      for (const w of shuffledPriorityPool) {
        if (!usedFr.has(w.fr) && !usedKab.has(w.kab)) {
          uniqueWrongOptions.push(w);
          usedFr.add(w.fr);
          usedKab.add(w.kab);
          if (uniqueWrongOptions.length === 3) break;
        }
      }

      if (uniqueWrongOptions.length < 3) {
        const shuffledGeneralPool = [...pool].sort(() => Math.random() - 0.5);
        for (const w of shuffledGeneralPool) {
          if (!usedFr.has(w.fr) && !usedKab.has(w.kab)) {
            uniqueWrongOptions.push(w);
            usedFr.add(w.fr);
            usedKab.add(w.kab);
            if (uniqueWrongOptions.length === 3) break;
          }
        }
      }

      while (uniqueWrongOptions.length < 3) {
        uniqueWrongOptions.push({ kab: '...', fr: '...', type: 'Inconnu' });
      }

      const shuffledOpts = [targetWord, ...uniqueWrongOptions].sort(
        () => Math.random() - 0.5
      );
      setCurrentQuestion(targetWord);
      setOptions(shuffledOpts);
      setFailedOptionKabs([]);
    },
    [currentLevelIdx, step, difficulty]
  );

  const startLevel = (idx: number) => {
    clickSFX();
    const startingHearts = difficulty === 'DEBUTANT' ? 5 : 3;
    setCurrentLevelIdx(idx);
    setStep(0);
    setSubStep(0);
    setRegionIntroStep(0);
    setHearts(startingHearts);
    setMaxHearts(startingHearts);
    setCombo(0);
    setFeedback(null);
    failedCurrentRunRef.current = [];
    setFailedCount(0);
    setCauldronLiberated(false);
    setGameState('REGION_INTRO');
  };

  const nextNpcDialogue = () => {
    clickSFX();
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
    isTypingRef.current = false;

    const lvlData = LEVEL_DATA[currentLevelIdx];
    const specialNode = lvlData.specialNodes && lvlData.specialNodes[step];
    
    if (lvlData.npcEncounter && step === lvlData.npcEncounter.step) {
      const maxSteps = (lvlData.npcEncounter.dialogues?.length || 1) - 1;
      if (npcDialogueStep < maxSteps) {
        setNpcDialogueStep(npcDialogueStep + 1);
      } else {
        setGameState('RUN');
        generateQuestion(false, currentLevelIdx, step, false, failedCurrentRunRef.current, 0);
      }
    } else if (specialNode) {
      const list = specialNode.npc.dialogues || (specialNode.npc.dialogue ? [specialNode.npc.dialogue] : []);
      const maxSteps = list.length - 1;
      if (npcDialogueStep < maxSteps) {
        setNpcDialogueStep(npcDialogueStep + 1);
      } else {
        setGameState('RUN');
        generateQuestion(false, currentLevelIdx, step, false, failedCurrentRunRef.current, 0);
      }
    } else {
      setGameState('RUN');
      generateQuestion(false, currentLevelIdx, step, false, failedCurrentRunRef.current, 0);
    }
  };

  const advanceToNextStep = (nextStep: number) => {
    playSound('step');
    setSubStep(0);
    const lvlData = LEVEL_DATA[currentLevelIdx];
    const npcData = lvlData.npcEncounter;
    const themes = lvlData.specialNodes || {};

    if (nextStep === 10) {
      setStep(nextStep);
      if (failedCurrentRunRef.current.length > 0) {
        setGameState('REVISION');
        playSound('revision');
      } else {
        setBossIntroStep(0);
        setGameState('BOSS_INTRO');
        playSound('boss');
      }
    } else if (npcData && nextStep === npcData.step) {
      setStep(nextStep);
      setGameState('NPC_DIALOGUE');
      setNpcDialogueStep(0);
    } else if (themes[nextStep]) {
      setStep(nextStep);
      playSound('theme');
      setGameState('NPC_DIALOGUE');
      setNpcDialogueStep(0);
    } else {
      setStep(nextStep);
      generateQuestion(false, currentLevelIdx, nextStep, false, failedCurrentRunRef.current, 0);
    }
  };

  const continueAfterChallengeVictory = () => {
    clickSFX();
    if (!challengeVictory) return;
    const next = challengeVictory.nextStep;
    setChallengeVictory(null);
    setSubStep(0);
    advanceToNextStep(next);
  };

  const handleAnswer = (selected: WordItem) => {
    if (feedback || !currentQuestion) return;
    if (failedOptionKabs.includes(selected.kab)) return;

    const isRev = gameState === 'REVISION_PLAY';

    if (selected.kab === currentQuestion.kab) {
      playSound('correct');

      if (!seenWordsRef.current.includes(currentQuestion.kab)) {
        seenWordsRef.current.push(currentQuestion.kab);
        localStorage.setItem('kabyleGame_seenWords', JSON.stringify(seenWordsRef.current));
      }

      const newCombo = combo + 1;
      setCombo(newCombo);
      const baseScore = difficulty === 'CONFIRME' ? 150 : 100;
      setScore((s) => s + baseScore * (newCombo >= 3 ? 2 : 1));

      setFeedback('CORRECT');
      setIsJumping(true);
      setIsWalking(true);

      // Flash blanc immédiat s'étalant sur les 2 écrans lors de la réactivation de la stèle au dernier niveau
      if (gameState === 'BOSS' && currentLevelIdx === 4) {
        setShowWhiteFlash(true);
        setTimeout(() => setShowWhiteFlash(false), 2000);
      }

      setTimeout(() => {
        setIsJumping(false);
        setIsWalking(false);
        setFeedback(null);

        if (gameState === 'BOSS') {
          playSound('stele');
          if (currentLevelIdx !== 4) {
            setStep(10);
          }

          const nextUnlocked = !unlockedLevels.includes(currentLevelIdx + 1) && currentLevelIdx <= 4
            ? [...unlockedLevels, currentLevelIdx + 1]
            : unlockedLevels;

          if (!unlockedLevels.includes(currentLevelIdx + 1) && currentLevelIdx <= 4) {
            setUnlockedLevels(nextUnlocked);
            if (currentLevelIdx < 4) setSelectedMapNode(currentLevelIdx + 1);
          }

          // Authoritative level-based progress save at the end of the level
          const earnedScore = (difficulty === 'CONFIRME' ? 150 : 100) * (newCombo >= 3 ? 2 : 1);
          const bossVictoryBonus = (currentLevelIdx + 1) * 500;
          const finalScore = score + earnedScore + bossVictoryBonus;
          setScore(finalScore);
          saveLevelProgress(playerId, {
            name: playerName,
            unlockedLevels: nextUnlocked,
            score: finalScore,
            unlockedSkins,
            currentSkin,
            seenWords: seenWordsRef.current,
            difficulty
          });

          // Trigger clear save announcement at level end
          setSaveAnnouncement({
            show: true,
            levelName: LEVEL_DATA[currentLevelIdx].name,
            levelNumber: currentLevelIdx + 1
          });

          setTimeout(() => {
            if (currentLevelIdx === 4) {
              setEpilogueStep(0);
              setCauldronLiberated(false);
              setGameState('EPILOGUE_ANIM');
              
              // Le chaudron change de couleur à la fin de la recolorisation (5s), avant l'apparition des PNJs
              setTimeout(() => {
                setCauldronLiberated(true);
                playSound('stele');
              }, 4800);

              setTimeout(() => {
                setStep(10);
                setGameState('EPILOGUE');
              }, 7000);
            } else {
              setVictoryStep(0);
              setGameState('VICTORY');
            }
          }, 1200);
          return;
        }

        if (isRev) {
          failedCurrentRunRef.current.shift();
          setFailedCount(failedCurrentRunRef.current.length);

          if (failedCurrentRunRef.current.length === 0) {
            setBossIntroStep(0);
            setGameState('BOSS_INTRO');
            playSound('boss');
          } else {
            generateQuestion(
              false,
              currentLevelIdx,
              step,
              true,
              failedCurrentRunRef.current
            );
          }
          return;
        }

        let shouldAdvanceStep = false;
        let nextSub = subStep;

        const currentThemes = LEVEL_DATA[currentLevelIdx].specialNodes || {};
        const isThemeNode = !!currentThemes[step];

        // Défis: 3 Q pour Débutant, 10 Q pour Confirmé
        const themeTarget = difficulty === 'DEBUTANT' ? 3 : 10;
        if (isThemeNode) {
          if (subStep < themeTarget - 1) {
            nextSub = subStep + 1;
            setSubStep(nextSub);
          } else {
            nextSub = themeTarget;
            setSubStep(nextSub);
            shouldAdvanceStep = true;
          }
        } else if (difficulty === 'CONFIRME') {
          if (subStep < 2) {
            nextSub = subStep + 1;
            setSubStep(nextSub);
          } else {
            nextSub = 0;
            setSubStep(nextSub);
            shouldAdvanceStep = true;
          }
        } else {
          shouldAdvanceStep = true;
        }

        if (shouldAdvanceStep) {
          const nextStep = step + 1;

          // Victoire de défi : Pop-up bien visible + gain d'un cœur si perdu !
          if (isThemeNode && currentThemes[step]) {
            playSound('victory');
            let heartRecovered = false;
            if (hearts < maxHearts) {
              setHearts((h) => Math.min(maxHearts, h + 1));
              heartRecovered = true;
            }
            const xpBonus = 250;
            setScore((s) => s + xpBonus);

            const themeData = currentThemes[step];
            setChallengeVictory({
              themeName: themeData.name,
              npcName: themeData.npc.name,
              heartGained: heartRecovered,
              xpGained: xpBonus,
              nextStep
            });
            return;
          }

          advanceToNextStep(nextStep);
        } else {
          generateQuestion(false, currentLevelIdx, step, false, failedCurrentRunRef.current, nextSub);
        }
      }, 1100);
    } else {
      playSound('wrong');
      setCombo(0);
      setFeedback('WRONG');
      setFailedOptionKabs((prev) => (prev.includes(selected.kab) ? prev : [...prev, selected.kab]));

      if (
        gameState === 'RUN' &&
        !failedCurrentRunRef.current.find((w) => w.kab === currentQuestion.kab)
      ) {
        failedCurrentRunRef.current.push(currentQuestion);
        setFailedCount(failedCurrentRunRef.current.length);
      }

      const remainingHearts = hearts - 1;
      setHearts(remainingHearts);

      setTimeout(() => {
        setFeedback(null);
        if (remainingHearts <= 0) {
          setGameState('GAME_OVER');
        }
      }, 2200);
    }
  };

  const currentLevelData = LEVEL_DATA[currentLevelIdx];
  const isEpilogueAnim = currentLevelIdx === 4 && gameState === 'EPILOGUE_ANIM';
  const isLevel5Restored = (
    currentLevelIdx === 4 && (
      gameState === 'EPILOGUE_ANIM' ||
      gameState === 'EPILOGUE' ||
      gameState === 'VICTORY' ||
      (unlockedLevels.includes(5) &&
        gameState !== 'BOSS' &&
        gameState !== 'BOSS_INTRO' &&
        gameState !== 'RUN' &&
        gameState !== 'REVISION' &&
        gameState !== 'REVISION_PLAY')
    )
  );
  const isLevel5VictoryOrRestored = currentLevelIdx === 4 && (
    isLevel5Restored ||
    isEpilogueAnim ||
    gameState === 'VICTORY' ||
    gameState === 'EPILOGUE'
  );
  // Dès que la sorcière dit "regardez le ciel au-dessus de nous !" (dialogue 6+), le ciel redevient sombre et dévoile la lune embrumée
  const isEpilogueDarkSky = currentLevelIdx === 4 && (
    (gameState === 'EPILOGUE' && epilogueStep >= 6) ||
    (gameState === 'VICTORY' && epilogueStep >= 6)
  );
  const displayLevelData = isLevel5VictoryOrRestored ? {
    ...currentLevelData,
    name: "Lǧemɛa n Tizi (Purifié)",
    steleIcon: "ⵣ",
    baseColor: "#16a34a", // green for restored
    regionIntro: "Le ciel est dégagé et l'air est à nouveau pur. Les villageois sont revenus et les souvenirs de la Kabylie ont été restaurés. C'est un lieu de célébration !"
  } : currentLevelData;
  const mapHeight = 1600;
  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewportHeight, setViewportHeight] = useState<number>(() =>
    typeof window !== 'undefined' ? window.innerHeight * 0.55 : 400
  );

  useEffect(() => {
    let rafId: number | null = null;
    let lastH = 0;

    const handleResize = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = null;
        let h = 0;
        if (viewportRef.current) {
          h = Math.round(viewportRef.current.clientHeight);
        } else if (typeof window !== 'undefined') {
          h = Math.round(window.innerHeight * 0.55);
        }
        if (h > 0 && Math.abs(h - lastH) >= 4) {
          lastH = h;
          setViewportHeight((prev) => (prev === h ? prev : h));
        }
      });
    };

    handleResize();
    window.addEventListener('resize', handleResize, { passive: true });
    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // Calculate dynamic camera offset based on current step and game state
  // to unveil the grand blue sky, sun, clouds, and majestic Kabyle mountain peaks without clipping
  const cameraOffsetY = useMemo(() => {
    if (
      step === 10 ||
      gameState === 'BOSS' ||
      gameState === 'BOSS_INTRO' ||
      gameState === 'VICTORY' ||
      gameState === 'EPILOGUE' ||
      gameState === 'EPILOGUE_ANIM'
    ) {
      // Place the summit stèle altar (at y=100) at ~70% of the viewport height,
      // giving generous, expansive space for the panoramic blue sky, glowing sun, and mountain vistas above
      const steleScreenTarget = viewportHeight * 0.70;
      return steleScreenTarget - 100;
    }

    if (step === 9) {
      const nodeScreenTarget = viewportHeight * 0.60;
      return nodeScreenTarget - 240;
    }

    if (step === 8) {
      const nodeScreenTarget = viewportHeight * 0.55;
      return nodeScreenTarget - 320;
    }

    const nodeY = PATH_NODES[step]?.y || 1210;
    return viewportHeight / 2 - nodeY;
  }, [step, gameState, viewportHeight]);
  
  const currentThemes = currentLevelData.specialNodes || {};

  const isFrToKabQuestion = difficulty === 'CONFIRME' || !!(currentThemes[step] && currentQuestion && getWordIcon(currentQuestion));

  const isGameplayState = 
    gameState === 'RUN' || 
    gameState === 'BOSS' || 
    gameState === 'REVISION' || 
    gameState === 'REVISION_PLAY' ||
    gameState === 'NPC_DIALOGUE' ||
    gameState === 'REGION_INTRO' ||
    gameState === 'BOSS_INTRO' ||
    gameState === 'VICTORY' ||
    gameState === 'EPILOGUE' ||
    gameState === 'EPILOGUE_ANIM';

  const isAdmin = (playerName || '').trim().toLowerCase() === 'admin' || playerId === ADMIN_PLAYER_ID;

  // Modal de mot de passe administrateur (AmawalAksil2026)
  const [showAdminPasswordModal, setShowAdminPasswordModal] = useState<boolean>(false);

  const handleAdminAuthSuccess = useCallback(() => {
    setShowAdminPasswordModal(false);
    switchPlayerProfile('Admin');
    triggerToast('👑 Mode Administrateur Déverrouillé !');
  }, []);

  // Grand overlay au début du jeu en mode normal (message de Yoann Baron et appel à contribution)
  // Apparaît à la première ouverture du jeu après avoir appuyé sur Commencer
  const [showCreatorWelcomeModal, setShowCreatorWelcomeModal] = useState<boolean>(false);
  const [isFirstLaunchFlow, setIsFirstLaunchFlow] = useState<boolean>(false);
  const [showTrainingGuideModal, setShowTrainingGuideModal] = useState<boolean>(false);
  const [showLegalRightsModal, setShowLegalRightsModal] = useState<boolean>(false);

  const openPlayerMessage = useCallback(() => {
    clickSFX();
    setIsFirstLaunchFlow(false);
    setShowCreatorWelcomeModal(true);
  }, []);

  const dismissCreatorWelcomeModal = useCallback(() => {
    setShowCreatorWelcomeModal(false);
    if (isFirstLaunchFlow) {
      setIsFirstLaunchFlow(false);
      try {
        localStorage.setItem('tamazight_first_run_tutorial_seen', 'true');
      } catch {}
      setShowTrainingGuideModal(true);
    }
  }, [isFirstLaunchFlow]);

  const dismissTrainingGuideModal = useCallback(() => {
    setShowTrainingGuideModal(false);
    try {
      localStorage.setItem('tamazight_first_run_tutorial_seen', 'true');
    } catch {}
  }, []);

  // First-time coaching overlay for Admin mode on Title Screen / Main Menu
  const [showAdminTitleOverlay, setShowAdminTitleOverlay] = useState<boolean>(() => {
    try {
      return !localStorage.getItem('tamazight_admin_title_hint_seen');
    } catch {
      return false;
    }
  });

  const dismissAdminTitleOverlay = useCallback(() => {
    setShowAdminTitleOverlay(false);
    try {
      localStorage.setItem('tamazight_admin_title_hint_seen', 'true');
    } catch {}
  }, []);

  // Overlay pédagogique au début du niveau 1 du mode aventure : consultation du dictionnaire Amawal
  const [showLevel1AmawalOverlay, setShowLevel1AmawalOverlay] = useState<boolean>(true);

  const dismissLevel1AmawalOverlay = useCallback(() => {
    setShowLevel1AmawalOverlay(false);
  }, []);

  const adminJumpToStep = (targetStep: number) => {
    if (!isAdmin) return;
    const nextStep = Math.max(0, Math.min(10, targetStep));
    clickSFX();

    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
      typingIntervalRef.current = null;
    }
    isTypingRef.current = false;
    setFeedback(null);
    setIsJumping(false);
    setIsWalking(false);
    setChallengeVictory(null);
    setSaveAnnouncement(null);
    setStep(nextStep);
    setSubStep(0);
    setNpcDialogueStep(0);
    setBossIntroStep(0);
    setRegionIntroStep(0);
    setVictoryStep(0);
    setHearts(maxHearts);
    failedCurrentRunRef.current = [];
    setFailedCount(0);

    const lvlData = LEVEL_DATA[currentLevelIdx];
    const npcData = lvlData.npcEncounter;
    const themes = lvlData.specialNodes || {};

    if (nextStep === 10) {
      setBossIntroStep(0);
      setGameState('BOSS');
      generateQuestion(true, currentLevelIdx, 10, false, [], 0);
      triggerToast(`✨ Téléportation Stèle Finale (Étape 10)`);
    } else if (npcData && nextStep === npcData.step) {
      setGameState('NPC_DIALOGUE');
      setNpcDialogueStep(0);
      triggerToast(`✨ Étape ${nextStep} (${npcData.name})`);
    } else if (themes[nextStep]) {
      setGameState('NPC_DIALOGUE');
      setNpcDialogueStep(0);
      triggerToast(`✨ Étape ${nextStep} (${themes[nextStep].name})`);
    } else {
      setGameState('RUN');
      generateQuestion(false, currentLevelIdx, nextStep, false, [], 0);
      triggerToast(`✨ Téléportation Étape ${nextStep}/10`);
    }
  };

  const adminAutoSolveQuestion = () => {
    if (!isAdmin) return;
    if (currentQuestion) {
      handleAnswer(currentQuestion);
      triggerToast(`✨ [Admin] Bonne réponse validée auto !`);
    } else {
      triggerToast(`⚠️ [Admin] Aucune question active.`);
    }
  };

  const adminFullHeal = () => {
    if (!isAdmin) return;
    playSound('correct');
    setHearts(maxHearts);
    triggerToast(`❤️ [Admin] Cœurs restaurés au max (${maxHearts}/${maxHearts}) !`);
  };

  const adminSwitchLevel = (targetLvl: number) => {
    if (!isAdmin) return;
    clickSFX();
    if (targetLvl === 5) {
      setGameState('CELESTIAL_CHALLENGE');
      triggerToast(`✨ Téléporté au Niveau 6 : Niveau Céleste (Ayur)`);
      return;
    }
    const cleanLvl = Math.max(0, Math.min(4, targetLvl));
    setCurrentLevelIdx(cleanLvl);
    setStep(0);
    setSubStep(0);
    setRegionIntroStep(0);
    setNpcDialogueStep(0);
    setBossIntroStep(0);
    setVictoryStep(0);
    setHearts(maxHearts);
    setCombo(0);
    setFeedback(null);
    setChallengeVictory(null);
    setSaveAnnouncement(null);
    failedCurrentRunRef.current = [];
    setFailedCount(0);
    setGameState('RUN');
    generateQuestion(false, cleanLvl, 0, false, [], 0);
    triggerToast(`✨ Téléporté au Niveau ${cleanLvl + 1} (${LEVEL_DATA[cleanLvl].name})`);
  };

  const adminRevive = () => {
    if (!isAdmin) return;
    playSound('correct');
    setHearts(maxHearts);
    setFeedback(null);
    setGameState('RUN');
    generateQuestion(false, currentLevelIdx, step, false, [], 0);
    triggerToast(`✨ Ressuscité avec succès !`);
  };

  const adminInstantVictory = () => {
    if (!isAdmin) return;
    playSound('stele');
    if (!unlockedLevels.includes(currentLevelIdx + 1) && currentLevelIdx < 4) {
      const newUnlocked = [...unlockedLevels, currentLevelIdx + 1];
      setUnlockedLevels(newUnlocked);
      autoSavePlayerData(playerId, { unlockedLevels: newUnlocked });
    } else if (currentLevelIdx === 4 && !unlockedLevels.includes(5)) {
      const newUnlocked = [...unlockedLevels, 5];
      setUnlockedLevels(newUnlocked);
      autoSavePlayerData(playerId, { unlockedLevels: newUnlocked });
    }
    setStep(10);
    setVictoryStep(0);
    setGameState('VICTORY');
    triggerToast(`✨ Niveau validé instantanément !`);
  };

  if (gameState === 'ADMIN_DASHBOARD') {
    return (
      <React.Suspense fallback={<div className="w-full h-full bg-[#0f172a] flex items-center justify-center text-white text-sm font-mono animate-pulse">Chargement Tableau de Bord...</div>}>
        <AdminDashboardView
          onBack={() => {
            clickSFX();
            setGameState('MAIN_MENU');
          }}
          onStartLevel={(lvlIdx) => {
            adminSwitchLevel(lvlIdx);
          }}
          onPlayerResetOrSwitch={() => {
            const activeProfile = getOrCreatePlayerProfile(playerId);
            setUnlockedLevels(activeProfile.unlockedLevels);
            setScore(activeProfile.score);
            setUnlockedSkins(activeProfile.unlockedSkins);
            setCurrentSkin(activeProfile.currentSkin);
            seenWordsRef.current = activeProfile.seenWords || [];
          }}
        />
      </React.Suspense>
    );
  }
  if (gameState === 'DICTIONARY') {
    return (
      <React.Suspense fallback={<div className="w-full h-full bg-[#fdf8f5] flex items-center justify-center text-slate-800 text-sm font-mono animate-pulse">Chargement de l'Amawal...</div>}>
        <DictionaryView
          score={score}
          onBack={closeDictionary}
          isAdmin={playerName === "Admin"}
          playerId={playerId}
          playerName={playerName}
          
          onAddScore={(xp) => {
            setScore((prev) => {
              const newScore = Math.max(0, prev + xp);
              autoSavePlayerData(playerId, { score: newScore });
              return newScore;
            });
          }}
        />
      </React.Suspense>
    );
  }
  if (gameState === 'CELESTIAL_CHALLENGE') {
    return (
      <React.Suspense fallback={<div className="w-full h-full bg-[#0f172a] flex items-center justify-center text-indigo-300 text-sm font-mono animate-pulse">Chargement du Défi Lunaire...</div>}>
        <CelestialMoonChallenge
          currentSkin={currentSkin}
          playerName={playerName}
          onBack={() => setGameState('WORLD_MAP')}
          onAddScore={(xp) => {
            setScore((prev) => {
              const newScore = prev + xp;
              autoSavePlayerData(playerId, { score: newScore });
              return newScore;
            });
          }}
          onComplete={() => {
            autoSavePlayerData(playerId, { score });
          }}
          onAdminSwitchLevel={adminSwitchLevel}
        />
      </React.Suspense>
    );
  }
  if (gameState === 'PROGRESSION') {
    return (
      <React.Suspense fallback={<div className="w-full h-full bg-[#1e293b] flex items-center justify-center text-slate-300 text-sm font-mono animate-pulse">Chargement de votre progression...</div>}>
        <ProgressionView
          score={score}
          playerName={playerName}
          onBack={() => {
            clickSFX();
            if (previousStateRef.current) {
              setGameState(previousStateRef.current);
            } else {
              setGameState('MAIN_MENU');
            }
          }}
        />
      </React.Suspense>
    );
  }
  if (gameState === 'INVENTORY') {
    return (
      <React.Suspense fallback={<div className="w-full h-full bg-[#1e293b] flex items-center justify-center text-slate-300 text-sm font-mono animate-pulse">Chargement de la Boutique...</div>}>
        <InventoryView
          onBack={closeInventory}
          score={score}
          setScore={setScore}
          unlockedSkins={unlockedSkins}
          setUnlockedSkins={setUnlockedSkins}
          currentSkin={currentSkin}
          setCurrentSkin={setCurrentSkin}
          unlockedLevels={unlockedLevels}
          initialTab={inventoryInitialTab}
          onStartCelestialChallenge={() => setGameState('CELESTIAL_CHALLENGE')}
          isAdmin={isAdmin}
        />
      </React.Suspense>
    );
  }
  if (gameState === 'CHALLENGES') {
    return (
      <React.Suspense fallback={<div className="w-full h-full bg-[#1e293b] flex items-center justify-center text-slate-300 text-sm font-mono animate-pulse">Chargement des Défis...</div>}>
        <ChallengesView
          onBack={() => {
            clickSFX();
            if (previousStateRef.current) {
              setGameState(previousStateRef.current);
            } else {
              setGameState('WORLD_MAP');
            }
          }}
          score={score}
          setScore={setScore}
          unlockedLevels={unlockedLevels}
          onStartCelestialChallenge={() => setGameState('CELESTIAL_CHALLENGE')}
          onOpenDictionary={() => {
            clickSFX();
            setGameState('DICTIONARY');
          }}
          isAdmin={isAdmin}
        />
      </React.Suspense>
    );
  }

  // Full-bleed on every screen (including adventure in-game and world map), except for the prologue cutscene (INTRO)
  const isFullBleedScreen = gameState !== 'INTRO';

  return (
    <div className={`w-full h-[100dvh] ${gameState === 'NAME_INPUT' ? 'bg-[#fbf7ee]' : gameState === 'ADVENTURE_TITLE' ? 'bg-[#050813]' : isGameplayState ? 'bg-[#020617]' : isFullBleedScreen ? 'bg-slate-900' : 'bg-black'} flex justify-center items-center overflow-hidden select-none`}>
      <div className={`w-full h-full flex flex-col font-mono text-stone-800 overflow-hidden relative ${
        isFullBleedScreen
          ? 'max-w-none shadow-none bg-transparent'
          : 'max-w-full md:max-w-5xl lg:max-w-6xl xl:max-w-7xl shadow-2xl bg-black'
      }`}>
        {/* Flash blanc plein écran s'étalant sur les 2 écrans lors de la réactivation de la stèle */}
        {showWhiteFlash && (
          <div className="fixed inset-0 bg-white z-[9999] animate-white-flash pointer-events-none" />
        )}

        {/* Flash de couleur unifié sur les 2 écrans lors d'une réponse (sans aucune ligne de séparation) */}
        {feedback === 'CORRECT' && (
          <div className="absolute inset-0 bg-white/30 z-[80] pointer-events-none flex items-center justify-center animate-in fade-in duration-100">
            <div className="absolute top-[27.5%] -translate-y-1/2 bg-black text-cyan-400 border-2 border-cyan-400 px-8 py-3 font-bold text-2xl uppercase tracking-widest shadow-[4px_4px_0_#22d3ee]">
              SAHIIT !
            </div>
          </div>
        )}
        {feedback === 'WRONG' && (
          <div className="absolute inset-0 bg-red-900/60 z-[80] pointer-events-none flex items-center justify-center animate-in fade-in duration-100">
            <div className="absolute top-[27.5%] -translate-y-1/2 bg-black text-red-500 border-2 border-red-500 px-8 py-3 font-bold text-2xl uppercase tracking-widest shadow-[4px_4px_0_red]">
              RATÉ !
            </div>
          </div>
        )}

        {/* Global Toast Notification */}
        {toastMessage && gameState !== 'VICTORY' && gameState !== 'EPILOGUE' && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-[100] max-w-[90vw] bg-slate-900 text-white font-bold text-xs sm:text-sm px-4 py-2.5 rounded-xl border-2 border-cyan-400 shadow-[0_0_20px_rgba(6,182,212,0.6)] flex items-center gap-2 animate-bounce">
            <span>{toastMessage}</span>
            <button type="button"
              onClick={() => setToastMessage(null)}
              className="text-slate-400 hover:text-white text-base ml-2 cursor-pointer"
            >
              ✕
            </button>
          </div>
        )}

      {/* Global Backgrounds */}
      {(gameState === 'TITLE' || gameState === 'MAIN_MENU') && (
        <div className="absolute inset-0 z-[5] overflow-hidden pointer-events-none flex items-center justify-center">
          <MainTitleScreenArtwork />
        </div>
      )}
      {gameState === 'ADVENTURE_TITLE' && (
        <div className="absolute inset-0 z-[5] overflow-hidden pointer-events-none flex items-center justify-center">
          <TitleScreenBackground />
        </div>
      )}

      {/* ========================================================= */}
      {/* 1. ÉCRAN DU HAUT (55%)                                    */}
      {/* ========================================================= */}
      <div 
        ref={viewportRef} 
        className={`relative w-full h-[55%] flex flex-col ${
          isGameplayState 
            ? 'overflow-visible z-10 -mb-[1px] bg-transparent' 
            : (gameState === 'TITLE' || gameState === 'MAIN_MENU')
            ? 'overflow-hidden z-20 bg-transparent shadow-none'
            : gameState === 'NAME_INPUT'
            ? 'overflow-hidden z-20 bg-[#fbf7ee] berber-weave-pattern'
            : gameState === 'ADVENTURE_TITLE'
            ? 'overflow-hidden z-20 bg-transparent shadow-none'
            : 'overflow-hidden z-20 bg-black shadow-[0_4px_10px_rgba(0,0,0,0.5)]'
        }`}
      >
        {gameState === 'NAME_INPUT' && (
          <div className="w-full h-full bg-[#fbf7ee] berber-weave-pattern flex flex-col items-center justify-between text-center text-stone-900 relative">
            <BerberRugBorder />
            <div className="flex flex-col items-center justify-center p-4">
              <div className="text-4xl sm:text-5xl mb-2 text-red-700 font-black animate-bounce">ⵣ</div>
              <h1 className="text-2xl sm:text-3xl font-cinzel font-black text-amber-950 tracking-wider uppercase drop-shadow-xs z-10 leading-tight">
                COMMENT T'APPELLES-TU ?
              </h1>
              <div className="text-xs sm:text-sm text-amber-900/90 mt-2 z-10 font-bold uppercase tracking-widest">
                Entre ton nom pour débuter la quête
              </div>
            </div>
            <BerberRugBorder />
          </div>
        )}
        {(gameState === 'TITLE' || gameState === 'MAIN_MENU') && (
          <div className="w-full h-full relative overflow-hidden bg-transparent">
            {/* Top Bar on Main Menu: Contact/Message Button, Music Toggle, Player Name & Progression */}
            {gameState === 'MAIN_MENU' && (
              <div className="absolute top-3 sm:top-4 inset-x-0 z-50 pointer-events-none px-3 sm:px-4">
                <div className="w-full max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto flex flex-col items-start gap-1 sm:gap-1.5 pointer-events-auto">
                  <div className="w-full flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={openPlayerMessage}
                        className="h-7 sm:h-8 text-[11px] sm:text-xs font-black px-3 sm:px-3.5 rounded-full border border-amber-400/80 bg-[#78350f]/85 hover:bg-[#92400e]/90 text-amber-200 shadow-md backdrop-blur-md transition-all cursor-pointer active:scale-95 shrink-0 flex items-center justify-center hover:border-amber-300"
                        title="Ouvrir le message de Yoann Baron aux joueurs"
                      >
                        Messages aux joueurs
                      </button>
                      <MusicToggleButton 
                        variant="compact" 
                        className="!h-7 sm:!h-8 !px-2.5 sm:!px-3 !py-0 !rounded-full !bg-[#78350f]/85 !border-amber-400/80 shrink-0 shadow-md text-white hover:!bg-[#92400e]/90" 
                      />
                    </div>

                    <span className="text-xs sm:text-sm font-black text-white whitespace-nowrap shrink-0 tracking-wide drop-shadow-[0_2px_4px_rgba(0,0,0,0.95)]">
                      {playerName || 'Voyageur'}
                    </span>
                  </div>

                  {/* Badge uniquement sous Message aux Joueurs */}
                  {(() => {
                    const currentTierIdx = LEVEL_THRESHOLDS.reduce((acc, t, i) => score >= t.xp ? i : acc, 0);
                    const currentTier = LEVEL_THRESHOLDS[currentTierIdx];

                    return (
                      <button
                        type="button"
                        onClick={() => openProgression()}
                        className="bg-transparent hover:bg-black/30 border-0 rounded-xl px-1.5 py-1 flex items-center gap-1.5 shadow-none transition-all active:scale-[0.98] cursor-pointer group text-left relative overflow-hidden shrink-0 z-10 drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]"
                        title="Cliquer pour voir le chemin de l'évolution et les 7 grades kabyles"
                      >
                        <div className="shrink-0 animate-gentle-float group-hover:scale-110 transition-transform">
                          <TierEmblem tierId={currentTier.id} size="sm" isUnlocked={true} isCurrent={true} />
                        </div>

                        <span className="font-cinzel font-black text-xs sm:text-sm text-amber-200 group-hover:text-amber-100 drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)]">
                          Rang {currentTierIdx + 1}/{LEVEL_THRESHOLDS.length}
                        </span>
                      </button>
                    );
                  })()}
                </div>
              </div>
            )}
          </div>
        )}

        {gameState === 'ADVENTURE_TITLE' && (
          <div className="w-full h-full relative bg-transparent overflow-hidden flex items-center justify-center">
            <TitleScreenArtwork />
          </div>
        )}

        {gameState === 'WORLD_MAP' && (
          <div className="w-full h-full relative bg-[#081528] overflow-hidden flex-1 flex flex-col">
            {/* Top Bar Header - Clean layout */}
            <div className="w-full bg-slate-950/95 border-b border-slate-700 px-3 py-1.5 flex items-center justify-between gap-2 z-30 shadow-md backdrop-blur-sm shrink-0">
              {/* Left group */}
              <div className="flex items-center gap-1.5 shrink-0">
                <MusicToggleButton variant="compact" />
              </div>

              {/* Center Title */}
              <div className="text-amber-300 text-xs sm:text-sm font-black uppercase tracking-widest text-center px-1">
                TAMURT N LEQBAYEL
              </div>

              {/* Right group */}
              <div className="flex items-center gap-1.5 shrink-0">
                {(unlockedLevels.includes(4) || unlockedLevels.includes(5) || isAdmin) && (
                  <button type="button"
                    onClick={() => {
                      clickSFX();
                      setGameState('CELESTIAL_CHALLENGE');
                    }}
                    title="Lancer l'Ultime Défi vers la Lune"
                    className="text-[10px] sm:text-xs px-2.5 py-1 font-black bg-gradient-to-r from-amber-500 to-yellow-400 hover:from-amber-400 hover:to-yellow-300 text-slate-950 border border-amber-300 rounded uppercase tracking-wider shadow-[0_0_12px_rgba(251,191,36,0.6)] cursor-pointer transition-all active:scale-95 flex items-center gap-1 shrink-0 animate-pulse"
                  >
                    <span>🌙</span>
                    <span>Ultime Défi</span>
                  </button>
                )}
                <button type="button"
                  onClick={openChallenges}
                  title="Ouvrir les Défis & Quêtes"
                  className="text-[10px] sm:text-xs px-2.5 py-1 font-black bg-amber-950/80 hover:bg-amber-900 text-amber-300 border border-amber-600 rounded uppercase tracking-wider shadow-[0_0_8px_rgba(245,158,11,0.3)] cursor-pointer transition-all active:scale-95 flex items-center gap-1 shrink-0"
                >
                  <span>🎯</span>
                  <span>Défis</span>
                </button>
              </div>
            </div>

            {/* Map Canvas - Full width desktop & mobile without black bars */}
            <div className="w-full flex-1 relative overflow-hidden bg-[#081528] flex items-center justify-center">
              <WorldMapArtwork
                unlockedLevels={unlockedLevels}
                selectedLevel={selectedMapNode}
                onSelect={handleNodeSelect}
                isAdmin={isAdmin}
              />
            </div>
          </div>
        )}

        {gameState === 'INTRO' && (
          <IntroCutsceneArtwork introStep={introStep} />
        )}
        
        {(gameState === 'REGION_INTRO' ||
          gameState === 'NPC_DIALOGUE' ||
          gameState === 'RUN' ||
          gameState === 'REVISION' ||
          gameState === 'REVISION_PLAY' ||
          gameState === 'BOSS_INTRO' ||
          gameState === 'BOSS' ||
          gameState === 'VICTORY' ||
          gameState === 'EPILOGUE' ||
          gameState === 'EPILOGUE_ANIM') && (
          <div 
            ref={gameContainerRef} 
            className={`w-full h-full relative flex-1 flex justify-center ${
              isGameplayState ? 'overflow-visible' : 'overflow-hidden'
            }`}
          >
            {/* Scrolling environment layer extending across the entire viewport width */}
            <div
              className="absolute left-0 w-full transition-transform duration-500 ease-linear pointer-events-none"
              style={{
                height: `${mapHeight + 800}px`,
                transform: `translate3d(0, ${cameraOffsetY}px, 0)`,
                willChange: 'transform'
              }}
            >
              {/* Ground checkerboard pattern extending across the entire viewport width */}
              <LevelGroundDamier
                levelId={currentLevelData.id}
                baseColor={isLevel5Restored ? (isEpilogueDarkSky ? '#27272a' : '#16a34a') : currentLevelData.baseColor}
                isRestored={isLevel5Restored}
                isNight={isEpilogueDarkSky}
              />

              {/* Full-width background panorama artwork (sky, mountains, hills, barrier) */}
              <LevelSurroundingArtwork
                levelId={currentLevelData.id}
                baseColor={isLevel5Restored ? (isEpilogueDarkSky ? '#27272a' : '#16a34a') : currentLevelData.baseColor}
                isRestored={isLevel5Restored}
                isNight={isEpilogueDarkSky}
              />
            </div>

            <div
              className="relative w-full max-w-[480px] h-full transition-transform duration-500 ease-linear"
              style={{
                height: `${mapHeight}px`,
                transform: `translate3d(0, ${cameraOffsetY}px, 0)`,
                willChange: 'transform'
              }}
            >
              {/* Ground board path, village houses, trees, altar, wild poppies */}
              <TexturedLevelArtwork
                levelId={currentLevelData.id}
                baseColor={isLevel5Restored ? (isEpilogueDarkSky ? '#27272a' : '#16a34a') : currentLevelData.baseColor}
                isRestored={isLevel5Restored}
                isNight={isEpilogueDarkSky}
                viewportWidth={viewportWidth}
              />

              {PATH_NODES.slice(0, 10).map((node, idx) => {
                const isCurrent = idx === step;
                const isPassed = idx < step;
                const isThemeNode = currentThemes[idx];
                const themeTarget = difficulty === 'DEBUTANT' ? 3 : 10;
                const isChallengeWon = Boolean(
                  isThemeNode && idx === step && (challengeVictory || subStep >= themeTarget)
                );

                return (
                  <div
                    key={idx}
                    className="absolute"
                    style={{
                      left: `${(node.x / 400) * 100}%`,
                      top: `${node.y}px`,
                      zIndex: 1,
                      transform: "translate(-50%, -50%) translateZ(0)"
                    }}
                  >
                    <div
                      className={`relative flex items-center justify-center transition-all overflow-hidden ${
                        isChallengeWon
                          ? 'w-8 h-8 bg-emerald-500 border-2 border-white shadow-[0_0_18px_#10b981]'
                          : isCurrent && gameState !== 'VICTORY'
                          ? 'w-10 h-10 border-2 border-white shadow-[0_0_15px_#22d3ee] bg-slate-800'
                          : isPassed || gameState === 'VICTORY'
                          ? 'w-6 h-6 bg-emerald-500 border border-white opacity-90'
                          : isThemeNode
                          ? 'w-8 h-8 bg-amber-400 border-2 border-white shadow-[0_0_10px_amber]'
                          : 'w-4 h-4 bg-black/40 border border-slate-400'
                      }`}
                      style={{ transform: 'rotate(45deg)' }}
                    >
                      {isChallengeWon ? (
                        <span className="absolute -rotate-45 text-xs font-black text-white drop-shadow">
                          ✓
                        </span>
                      ) : isCurrent && gameState !== 'VICTORY' ? (
                        <>
                          <div 
                            className="absolute"
                            style={{ 
                              width: '150%', height: '150%', 
                              transform: 'rotate(-45deg)' 
                            }}
                          >
                            <div 
                              className="absolute bottom-0 left-0 w-full bg-cyan-400 transition-all duration-500 ease-out"
                              style={{ 
                                height: `${Math.min(100, (((feedback === 'CORRECT' ? subStep + 1 : subStep)) / (isThemeNode ? themeTarget : (difficulty === 'CONFIRME' ? 3 : 1))) * 100)}%` 
                              }}
                            />
                          </div>
                          <div className="w-3 h-3 bg-white rotate-45 animate-pulse rounded-sm z-10" />
                        </>
                      ) : null}
                      {isThemeNode && !isCurrent && !isPassed && !isChallengeWon && (
                        <span className="absolute -rotate-45 text-sm sm:text-base font-black text-slate-950 select-none leading-none drop-shadow-[0_1px_1px_rgba(255,255,255,0.4)] flex items-center justify-center">
                          ⵣ
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Standard Level NPC Encounter - Stays on the spot beside node and bounces when talking */}
              {currentLevelData.npcEncounter && (() => {
                const enc = currentLevelData.npcEncounter;
                const isAtNpc = step === enc.step;
                const nodePos = PATH_NODES[enc.step];
                const posX = (nodePos.x / 400) * 100 + 8;
                const posY = nodePos.y + 6;

                return (
                  <div
                    className="absolute z-10 -translate-x-1/2 -translate-y-full flex flex-col items-center pointer-events-none"
                    style={{
                      left: `${posX}%`,
                      top: `${posY}px`,
                      zIndex: Math.floor(posY)
                    }}
                  >
                    {isAtNpc && gameState === 'NPC_DIALOGUE' && (
                      <div className="text-[10px] bg-slate-900 text-cyan-300 px-1.5 py-0.5 rounded border border-cyan-400 font-bold mb-1 animate-bounce shadow-md">
                        💬 {enc.name}
                      </div>
                    )}
                    <NpcSprite
                      type={enc.sprite}
                      isWalking={isAtNpc && gameState === 'NPC_DIALOGUE'}
                      flip={false}
                    />
                  </div>
                );
              })()}

              {/* Theme Challenge NPCs - Stays on the spot beside node and bounces when talking */}
              {Object.entries(currentThemes).map(([s, nodeData]) => {
                const nodeStep = parseInt(s);
                const isAtNode = step === nodeStep;
                const nodePos = PATH_NODES[nodeStep];
                const posX = (nodePos.x / 400) * 100 - 8;
                const posY = nodePos.y + 6;

                return (
                  <div
                    key={`theme-npc-${nodeStep}`}
                    className="absolute z-10 -translate-x-1/2 -translate-y-full flex flex-col items-center pointer-events-none"
                    style={{
                      left: `${posX}%`,
                      top: `${posY}px`,
                      zIndex: Math.floor(posY)
                    }}
                  >
                    {isAtNode && gameState === 'NPC_DIALOGUE' && (
                      <div className="text-[10px] bg-slate-900 text-amber-300 px-1.5 py-0.5 rounded border border-amber-400 font-bold mb-1 animate-bounce shadow-md">
                        ⭐ {nodeData.npc.name}
                      </div>
                    )}
                    <NpcSprite
                      type={nodeData.npc.sprite}
                      isWalking={isAtNode && gameState === 'NPC_DIALOGUE'}
                      flip={false}
                    />
                  </div>
                );
              })}

              {/* Sacred Stele & Progressive Blue Diamond Altar at Node 10 */}
              <div
                className="absolute z-10 -translate-x-1/2 -translate-y-full flex flex-col items-center transition-all duration-1000"
                style={{
                  left: `${(PATH_NODES[10].x / 400) * 100}%`,
                  top: `${PATH_NODES[10].y + 10}px`
                }}
              >
                <SteleAltarMonument
                  isActivated={gameState === 'VICTORY' || gameState === 'EPILOGUE' || gameState === 'EPILOGUE_ANIM'}
                  isCurrent={step === 10}
                  steleIcon={displayLevelData.steleIcon}
                  levelName={currentLevelData.name}
                  isDestroyed={currentLevelIdx === 4 && gameState !== 'VICTORY' && gameState !== 'EPILOGUE' && !isLevel5Restored}
                />
              </div>

              {/* Level 5 Summit Staging: Jedjiga (La Sorcière) and the Broken Cauldron in front of the Destroyed Stele */}
              {currentLevelIdx === 4 && (gameState === 'BOSS_INTRO' || gameState === 'BOSS' || gameState === 'EPILOGUE_ANIM' || gameState === 'EPILOGUE' || gameState === 'VICTORY' || isLevel5Restored) && (() => {
                const isEpilogueOrPeace = gameState === 'EPILOGUE' || (gameState === 'VICTORY' && currentLevelIdx === 4) || (isLevel5Restored && gameState !== 'EPILOGUE_ANIM' && gameState !== 'BOSS' && gameState !== 'BOSS_INTRO' && gameState !== 'REVISION' && gameState !== 'REVISION_PLAY' && gameState !== 'RUN');

                return (
                  <>
                    {/* The Cauldron placed on summit - changes color at the end of recolorisation before NPCs appear */}
                    <div
                      className="absolute -translate-x-1/2 -translate-y-full transition-all duration-1000 pointer-events-none"
                      style={{
                        left: `${(PATH_NODES[10].x / 400) * 100 + 12}%`,
                        top: `${PATH_NODES[10].y + 36}px`,
                        zIndex: 10
                      }}
                    >
                      <BrokenCauldronToxicProps
                        isLiberated={cauldronLiberated || isLevel5Restored || isEpilogueOrPeace}
                      />
                    </div>

                    {/* Witch Jedjiga - stays visible throughout recolorisation at the stele and smoothly glides to her spot next to the player in fondue when epilogue/NPCs appear */}
                    <div
                      className="absolute transition-all duration-1000 ease-out z-50 drop-shadow-xl flex flex-col items-center -translate-x-1/2 -translate-y-full pointer-events-none"
                      style={{
                        left: isEpilogueOrPeace
                          ? `${(PATH_NODES[10].x / 400) * 100 + 8.5}%`
                          : `${(PATH_NODES[10].x / 400) * 100 - 14}%`,
                        top: isEpilogueOrPeace
                          ? `${PATH_NODES[10].y + 64}px`
                          : `${PATH_NODES[10].y + 24}px`,
                        zIndex: isEpilogueOrPeace ? 60 : Math.floor(PATH_NODES[10].y) + 8
                      }}
                    >
                      {((gameState === 'BOSS_INTRO' || gameState === 'BOSS') ||
                        (isEpilogueOrPeace && currentSpeaker?.speaker?.toLowerCase().includes('jedjiga'))) && (
                        <div className="text-[10px] bg-slate-900 text-purple-300 px-2 py-0.5 rounded-full border border-purple-400 font-bold mb-1 animate-bounce shadow-lg whitespace-nowrap z-50">
                          🧙‍♀️ Jedjiga
                        </div>
                      )}
                      <NpcSprite
                        type="witch"
                        isWalking={
                          gameState === 'BOSS_INTRO' ||
                          gameState === 'BOSS' ||
                          (isEpilogueOrPeace && currentSpeaker?.speaker?.toLowerCase().includes('jedjiga'))
                        }
                        flip={false}
                      />
                    </div>
                  </>
                );
              })()}

              {/* Player Sprite with offset on summit to stand back with perspective */}
              <div
                className="absolute transition-all duration-500 ease-linear flex flex-col items-center"
                style={{
                  left: `${(PATH_NODES[step]?.x / 400) * 100}%`,
                  top: `${
                    step === 10
                      ? PATH_NODES[step]?.y + 64
                      : PATH_NODES[step]?.y + 8
                  }px`,
                  zIndex: 9999,
                  transform: "translate(-50%, -100%) translateZ(10px)"
                }}
              >
                {(currentSpeaker?.speaker?.toLowerCase().includes('pensée') ||
                  (playerName && currentSpeaker?.speaker?.toLowerCase().includes(playerName.toLowerCase()))) && (
                  <div className="text-[10px] bg-slate-900 text-blue-300 px-2 py-0.5 rounded-full border border-blue-400 font-bold mb-1 animate-bounce shadow-lg whitespace-nowrap z-50">
                    {currentSpeaker.icon || '💭'} {playerName || 'Toi'}
                  </div>
                )}
                <PlayerSprite isWalking={isWalking} isJumping={isJumping} skin={currentSkin} />
              </div>

              {/* Portique d'entrée en premier plan : le personnage passe bien SOUS le portique */}
              <svg
                className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-visible"
                viewBox="0 0 400 1600"
                preserveAspectRatio="none"
                style={{ zIndex: 10005 }}
              >
                <LevelEntrancePorticoOverlay
                  levelId={currentLevelData.id}
                  isRestored={isLevel5Restored}
                  isNight={isEpilogueDarkSky}
                />
              </svg>

              {(gameState === 'EPILOGUE' || (gameState === 'VICTORY' && currentLevelIdx === 4) || (isLevel5Restored && gameState !== 'EPILOGUE_ANIM' && gameState !== 'BOSS' && gameState !== 'BOSS_INTRO' && gameState !== 'REVISION' && gameState !== 'REVISION_PLAY' && gameState !== 'RUN')) && (
                <>
                  {/* Peaceful gathering of villagers alongside player and witch around the awakened stele */}
                  {/* Left Wing 1: Tamedyazt (La Poétesse d'Iɣil) */}
                  <div
                    className="absolute animate-fade-in-3s transition-all duration-1000 ease-out z-50 drop-shadow-xl flex flex-col items-center -translate-x-1/2 -translate-y-full pointer-events-none"
                    style={{ left: `${(PATH_NODES[10].x / 400) * 100 - 36}%`, top: `${PATH_NODES[10].y + 64}px`, zIndex: 60 }}
                  >
                    {(gameState === 'EPILOGUE' || gameState === 'VICTORY') &&
                      (currentSpeaker?.speaker?.toLowerCase().includes('tamedyazt') ||
                        currentSpeaker?.speaker?.toLowerCase().includes('poétesse') ||
                        currentSpeaker?.speaker?.toLowerCase().includes('poete')) && (
                        <div className="text-[10px] bg-slate-900 text-purple-300 px-2 py-0.5 rounded-full border border-purple-400 font-bold mb-1 animate-bounce shadow-lg whitespace-nowrap z-50">
                          🪕 Tamedyazt
                        </div>
                      )}
                    <NpcSprite
                      type="poet"
                      isWalking={
                        (gameState === 'EPILOGUE' || gameState === 'VICTORY') &&
                        (currentSpeaker?.speaker?.toLowerCase().includes('tamedyazt') ||
                          currentSpeaker?.speaker?.toLowerCase().includes('poétesse') ||
                          currentSpeaker?.speaker?.toLowerCase().includes('poete'))
                      }
                      flip={false}
                    />
                  </div>

                  {/* Left Wing 2: Aɛekkaf (L'Ermite d'Adrar) */}
                  <div
                    className="absolute animate-fade-in-3s transition-all duration-1000 ease-out z-50 drop-shadow-xl flex flex-col items-center -translate-x-1/2 -translate-y-full pointer-events-none"
                    style={{ left: `${(PATH_NODES[10].x / 400) * 100 - 27}%`, top: `${PATH_NODES[10].y + 64}px`, zIndex: 60 }}
                  >
                    {(gameState === 'EPILOGUE' || gameState === 'VICTORY') &&
                      (currentSpeaker?.speaker?.toLowerCase().includes('aɛekkaf') ||
                        currentSpeaker?.speaker?.toLowerCase().includes('ermite')) && (
                        <div className="text-[10px] bg-slate-900 text-slate-200 px-2 py-0.5 rounded-full border border-slate-400 font-bold mb-1 animate-bounce shadow-lg whitespace-nowrap z-50">
                          ⛰️ Aɛekkaf
                        </div>
                      )}
                    <NpcSprite
                      type="hermit"
                      isWalking={
                        (gameState === 'EPILOGUE' || gameState === 'VICTORY') &&
                        (currentSpeaker?.speaker?.toLowerCase().includes('aɛekkaf') ||
                          currentSpeaker?.speaker?.toLowerCase().includes('ermite'))
                      }
                      flip={false}
                    />
                  </div>

                  {/* Left Wing 3: Na Faḍma (L'Ancienne d'Ighzer) */}
                  <div
                    className="absolute animate-fade-in-3s transition-all duration-1000 ease-out z-50 drop-shadow-xl flex flex-col items-center -translate-x-1/2 -translate-y-full pointer-events-none"
                    style={{ left: `${(PATH_NODES[10].x / 400) * 100 - 18}%`, top: `${PATH_NODES[10].y + 64}px`, zIndex: 60 }}
                  >
                    {(gameState === 'EPILOGUE' || gameState === 'VICTORY') &&
                      (currentSpeaker?.speaker?.toLowerCase().includes('faḍma') ||
                        currentSpeaker?.speaker?.toLowerCase().includes('fadhma') ||
                        currentSpeaker?.speaker?.toLowerCase().includes('femme')) && (
                        <div className="text-[10px] bg-slate-900 text-pink-300 px-2 py-0.5 rounded-full border border-pink-400 font-bold mb-1 animate-bounce shadow-lg whitespace-nowrap z-50">
                          👵 Na Faḍma
                        </div>
                      )}
                    <NpcSprite
                      type="old_woman"
                      isWalking={
                        (gameState === 'EPILOGUE' || gameState === 'VICTORY') &&
                        (currentSpeaker?.speaker?.toLowerCase().includes('faḍma') ||
                          currentSpeaker?.speaker?.toLowerCase().includes('fadhma') ||
                          currentSpeaker?.speaker?.toLowerCase().includes('femme'))
                      }
                      flip={false}
                    />
                  </div>

                  {/* Left Wing 4: Amusnaw (Le Sage de Taddart) */}
                  <div
                    className="absolute animate-fade-in-3s transition-all duration-1000 ease-out z-50 drop-shadow-xl flex flex-col items-center -translate-x-1/2 -translate-y-full pointer-events-none"
                    style={{ left: `${(PATH_NODES[10].x / 400) * 100 - 9}%`, top: `${PATH_NODES[10].y + 64}px`, zIndex: 60 }}
                  >
                    {(gameState === 'EPILOGUE' || (gameState === 'VICTORY' && currentLevelIdx === 4)) &&
                      currentSpeaker?.speaker?.toLowerCase().includes('amusnaw') && (
                        <div className="text-[10px] bg-slate-900 text-cyan-300 px-2 py-0.5 rounded-full border border-cyan-400 font-bold mb-1 animate-bounce shadow-lg whitespace-nowrap z-50">
                          👴 Amusnaw
                        </div>
                      )}
                    <NpcSprite
                      type="old_man"
                      isWalking={
                        (gameState === 'EPILOGUE' || (gameState === 'VICTORY' && currentLevelIdx === 4)) &&
                        currentSpeaker?.speaker?.toLowerCase().includes('amusnaw')
                      }
                      flip={false}
                    />
                  </div>

                  {/* Right Wing 1: Dda Sliman (Le Fermier d'Ighzer) */}
                  <div
                    className="absolute animate-fade-in-3s transition-all duration-1000 ease-out z-50 drop-shadow-xl flex flex-col items-center -translate-x-1/2 -translate-y-full pointer-events-none"
                    style={{ left: `${(PATH_NODES[10].x / 400) * 100 + 17}%`, top: `${PATH_NODES[10].y + 64}px`, zIndex: 60 }}
                  >
                    {(gameState === 'EPILOGUE' || gameState === 'VICTORY') &&
                      (currentSpeaker?.speaker?.toLowerCase().includes('sliman') ||
                        currentSpeaker?.speaker?.toLowerCase().includes('fermier')) && (
                        <div className="text-[10px] bg-slate-900 text-amber-300 px-2 py-0.5 rounded-full border border-amber-400 font-bold mb-1 animate-bounce shadow-lg whitespace-nowrap z-50">
                          🌾 Dda Sliman
                        </div>
                      )}
                    <NpcSprite
                      type="farmer"
                      isWalking={
                        (gameState === 'EPILOGUE' || gameState === 'VICTORY') &&
                        (currentSpeaker?.speaker?.toLowerCase().includes('sliman') ||
                          currentSpeaker?.speaker?.toLowerCase().includes('fermier'))
                      }
                      flip={true}
                    />
                  </div>

                  {/* Right Wing 2: Ameksa (Le Berger d'Adrar) */}
                  <div
                    className="absolute animate-fade-in-3s transition-all duration-1000 ease-out z-50 drop-shadow-xl flex flex-col items-center -translate-x-1/2 -translate-y-full pointer-events-none"
                    style={{ left: `${(PATH_NODES[10].x / 400) * 100 + 26}%`, top: `${PATH_NODES[10].y + 64}px`, zIndex: 60 }}
                  >
                    {(gameState === 'EPILOGUE' || gameState === 'VICTORY') &&
                      (currentSpeaker?.speaker?.toLowerCase().includes('berger') ||
                        currentSpeaker?.speaker?.toLowerCase().includes('ameksa')) && (
                        <div className="text-[10px] bg-slate-900 text-emerald-300 px-2 py-0.5 rounded-full border border-emerald-400 font-bold mb-1 animate-bounce shadow-lg whitespace-nowrap z-50">
                          🐑 Ameksa
                        </div>
                      )}
                    <NpcSprite
                      type="shepherd"
                      isWalking={
                        (gameState === 'EPILOGUE' || gameState === 'VICTORY') &&
                        (currentSpeaker?.speaker?.toLowerCase().includes('berger') ||
                          currentSpeaker?.speaker?.toLowerCase().includes('ameksa'))
                      }
                      flip={true}
                    />
                  </div>
                </>
              )}
            </div>

            <WeatherCanvas 
              weatherType={currentLevelData.weather} 
              expandToBottom={isGameplayState}
            />
            <StrangeFog
              isLiberated={gameState === 'VICTORY' || gameState === 'EPILOGUE' || gameState === 'EPILOGUE_ANIM'}
              levelId={currentLevelData.id}
              expandToBottom={isGameplayState}
            />

            {(gameState === 'RUN' ||
              gameState === 'REVISION' ||
              gameState === 'REVISION_PLAY' ||
              gameState === 'BOSS' ||
              gameState === 'NPC_DIALOGUE') && (
              <>
                <div className="absolute top-3 left-3 flex flex-col items-start gap-1 z-40 pointer-events-none">
                  <div className="bg-black/80 border border-slate-600 p-1.5 flex gap-1 rounded pointer-events-auto">
                    {Array.from({ length: maxHearts }).map((_, i) => (
                      <div
                        key={i}
                        className={`w-3.5 h-3.5 ${
                          i < hearts ? 'bg-red-500' : 'bg-slate-800'
                        } border border-black`}
                      />
                    ))}
                  </div>
                  <div className="text-white text-xs sm:text-sm font-bold tracking-wide drop-shadow-[0_1px_3px_rgba(0,0,0,0.95)] px-0.5 select-none">
                    {playerName || 'Voyageur'}
                  </div>
                </div>

                <div className="absolute top-3 right-3 bg-black/80 border border-slate-600 px-3 py-1.5 text-white text-xs font-bold z-40 rounded flex flex-col items-end">
                  <span>
                    XP: <span className="text-cyan-400">{score}</span>
                  </span>
                  {combo > 1 && (
                    <span className="text-[10px] text-orange-400 animate-pulse mt-0.5">
                      COMBO x{combo}
                    </span>
                  )}
                </div>

                <div className="absolute top-16 right-3 flex flex-col items-center z-40">
                  <div className="bg-black/80 border border-slate-600 w-8 h-8 flex items-center justify-center rounded-full mb-2 shadow-lg">
                    <span className="text-sm">{displayLevelData.steleIcon}</span>
                  </div>
                  <div className="w-2.5 h-32 bg-slate-800 border border-slate-600 rounded-full relative overflow-hidden shadow-inner">
                    <div 
                       className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-cyan-600 to-cyan-400 transition-all duration-500 ease-out"
                       style={{ height: `${(step / 10) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 flex items-center justify-center gap-1 sm:gap-1.5 z-50 w-full max-w-[calc(100%-1.25rem)] px-1">
                  <button type="button"
                    onClick={() => {
                      clickSFX();
                      setGameState('MAIN_MENU');
                    }}
                    className="text-[9px] sm:text-[10px] text-white uppercase tracking-wider font-black bg-red-900/90 hover:bg-red-800/90 px-2 py-1 sm:px-2.5 sm:py-1 rounded-full border border-red-500/50 transition-colors shadow-sm backdrop-blur-sm cursor-pointer whitespace-nowrap shrink-0 flex items-center gap-1 active:scale-95"
                  >
                    <svg className="w-2.5 h-2.5 fill-current shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                      <polygon points="17,4 7,12 17,20" />
                    </svg>
                    <span>Menu</span>
                  </button>
                  <button type="button"
                    onClick={() => {
                      clickSFX();
                      setGameState('WORLD_MAP');
                    }}
                    className="text-[9px] sm:text-[10px] text-cyan-300 uppercase tracking-wider font-bold bg-black/85 hover:bg-slate-900/90 px-2 py-1 sm:px-2.5 sm:py-1 rounded-full border border-cyan-500/40 transition-colors shadow-sm backdrop-blur-sm cursor-pointer whitespace-nowrap shrink-0 flex items-center gap-1"
                  >
                    <span>🗺️</span> <span>Carte</span>
                  </button>
                  <button type="button"
                    onClick={openDictionary}
                    className={`text-[9px] sm:text-[10px] text-amber-300 uppercase tracking-wider font-bold bg-black/85 hover:bg-slate-900/90 px-2 py-1 sm:px-2.5 sm:py-1 rounded-full border transition-colors shadow-sm backdrop-blur-sm cursor-pointer whitespace-nowrap shrink-0 flex items-center gap-1 ${
                      currentLevelIdx === 0
                        ? 'border-amber-400 shadow-[0_0_10px_rgba(251,191,36,0.5)] ring-1 ring-amber-400/40'
                        : 'border-amber-500/40'
                    }`}
                  >
                    <span>📖</span> <span>Amawal</span>
                  </button>
                  <button type="button"
                    onClick={openInventory}
                    className="text-[9px] sm:text-[10px] text-fuchsia-300 uppercase tracking-wider font-bold bg-black/85 hover:bg-slate-900/90 px-2 py-1 sm:px-2.5 sm:py-1 rounded-full border border-fuchsia-500/40 transition-colors shadow-sm backdrop-blur-sm cursor-pointer whitespace-nowrap shrink-0 flex items-center gap-1"
                  >
                    <span>🎒</span> <span>Inventaire</span>
                  </button>
                  <MusicToggleButton variant="compact" className="!p-1 sm:!p-1.5 !rounded-full !bg-black/85 !border-slate-700 shrink-0 shadow-sm text-white" />
                </div>
              </>
            )}

            {currentThemes[step] && (gameState === 'RUN' || challengeVictory) && !feedback && (
              <div
                className={`absolute top-24 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full z-[10000] font-black text-[10px] uppercase tracking-widest border-2 transition-all shadow-lg ${
                  challengeVictory || subStep >= (difficulty === 'DEBUTANT' ? 3 : 10)
                    ? 'bg-emerald-500 text-slate-950 border-emerald-200 shadow-[0_0_16px_rgba(16,185,129,0.85)] animate-pulse'
                    : 'bg-amber-400 text-amber-950 border-amber-200 shadow-[0_2px_10px_rgba(251,191,36,0.5)]'
                }`}
              >
                {challengeVictory || subStep >= (difficulty === 'DEBUTANT' ? 3 : 10)
                  ? 'Victoire'
                  : `Défi : ${subStep + 1}/${difficulty === 'DEBUTANT' ? 3 : 10}`}
              </div>
            )}

            {gameState === 'REVISION' && !feedback && (
              <div className="absolute top-1/4 left-0 w-full bg-orange-950/90 border-y-4 border-orange-500 text-orange-400 text-center py-3 z-40 font-bold tracking-widest animate-pulse shadow-lg">
                RÉVISION ({failedCount} restants)
              </div>
            )}
          </div>
        )}

        {gameState === 'GAME_OVER' && (
          <div className="w-full h-full bg-black p-6 flex flex-col items-center justify-center text-center relative">
            <h2 className="text-4xl font-bold text-red-600 tracking-widest uppercase mb-2">
              Échec
            </h2>
            <div className="text-slate-400 text-sm">Le savoir retourne à la poussière...</div>
          </div>
        )}
      </div>

      {/* ========================================================= */}
      {/* 2. ÉCRAN DU BAS (45%)                                     */}
      {/* ========================================================= */}
      <div className={`relative w-full h-[45%] flex flex-col z-20 ${
        isGameplayState
          ? 'bg-transparent border-t-0 shadow-none overflow-visible' 
          : gameState === 'WORLD_MAP'
            ? 'bg-transparent border-t-0 shadow-none overflow-visible'
            : (gameState === 'TITLE' || gameState === 'MAIN_MENU')
            ? 'bg-transparent border-t-0 shadow-none overflow-hidden'
            : gameState === 'ADVENTURE_TITLE'
            ? 'bg-transparent border-t-0 shadow-none overflow-hidden'
            : gameState === 'NAME_INPUT'
            ? 'bg-[#fbf7ee] berber-weave-pattern border-t-0 shadow-none overflow-hidden text-stone-900'
            : 'bg-slate-950 border-t-2 border-slate-800 overflow-hidden'
      }`}>
        <div className="w-full h-full pt-2 pb-6 sm:pb-8 md:pb-10 px-3 sm:px-6 flex flex-col relative z-10 overflow-y-auto">
          {gameState === 'TITLE' && (
            <div className="w-full h-full flex flex-col justify-center items-center gap-3 md:gap-5 px-4 sm:px-8 max-w-xl md:max-w-2xl mx-auto relative pb-4 sm:pb-8">
              <button type="button"
                onClick={() => {
                  clickSFX();
                  if (!playerName || playerName === 'Voyageur') {
                    setTempName('');
                    setGameState('NAME_INPUT');
                  } else {
                    setGameState('MAIN_MENU');
                    if (!isAdmin) {
                      const hasSeenFirstRun = (() => {
                        try {
                          return localStorage.getItem('tamazight_first_run_tutorial_seen') === 'true';
                        } catch {
                          return false;
                        }
                      })();
                      if (!hasSeenFirstRun) {
                        setIsFirstLaunchFlow(true);
                        setShowCreatorWelcomeModal(true);
                      }
                    }
                  }
                }}
                className="w-full max-w-md md:max-w-xl py-4 sm:py-5 md:py-6 bg-gradient-to-r from-[#92400e]/85 via-[#b45309]/85 to-[#92400e]/85 hover:from-[#78350f]/90 hover:via-[#92400e]/90 hover:to-[#78350f]/90 backdrop-blur-md text-amber-50 font-cinzel font-black text-xl sm:text-2xl md:text-3xl uppercase tracking-[0.2em] border-2 border-amber-400 hover:border-yellow-200 animate-menu-glow shadow-[0_4px_30px_rgba(0,0,0,0.6)] active:scale-95 transition-all rounded-2xl cursor-pointer flex items-center justify-center gap-3 group relative overflow-hidden"
              >
                {/* Diagonal Luminous Reflection Shimmer */}
                <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-amber-100/30 to-transparent pointer-events-none animate-menu-shimmer" />
                
                <span className="text-amber-300 text-lg sm:text-xl md:text-2xl group-hover:scale-125 transition-transform drop-shadow-[0_0_8px_#fde047]">ⵣ</span>
                <span className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.7)] text-amber-100 group-hover:text-white">COMMENCER</span>
                <span className="text-amber-300 text-lg sm:text-xl md:text-2xl group-hover:scale-125 transition-transform drop-shadow-[0_0_8px_#fde047]">ⵣ</span>
              </button>
              <div className="text-[11px] sm:text-xs md:text-sm text-amber-300/90 font-cinzel font-semibold uppercase tracking-[0.2em] text-center animate-pulse">
                ✦ Appuie pour entrer • Quizz Kabyle : Aventures & Défis ✦
              </div>
              <div className="flex flex-col items-center justify-center gap-1.5 pt-1">
                <div className="flex items-center justify-center gap-1.5">
                  <span className="text-[10px] sm:text-xs text-slate-400 font-cinzel tracking-wider uppercase">Créé par</span>
                  <span className="text-[11px] sm:text-xs text-amber-300 font-cinzel font-black tracking-widest uppercase drop-shadow-[0_0_8px_rgba(253,224,71,0.6)]">Yoann Baron</span>
                </div>
                <div className="flex items-center justify-center pt-0.5">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      clickSFX();
                      setShowLegalRightsModal(true);
                    }}
                    className="text-[10px] sm:text-[11px] text-amber-300/70 hover:text-amber-200 font-cinzel tracking-wide transition-colors cursor-pointer flex items-center gap-1 underline underline-offset-2 opacity-85 hover:opacity-100"
                  >
                    <span>© 2026 Mentions Légales & Sources</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {gameState === 'ADVENTURE_TITLE' && (
            <div className="w-full h-full flex flex-col justify-between items-center gap-2.5 md:gap-4 py-1 sm:py-2 px-2 sm:px-6 max-w-xl md:max-w-2xl mx-auto pb-6 sm:pb-8 md:pb-10">
              {/* Header */}
              <div className="flex items-center justify-center pb-1.5 w-full flex-shrink-0">
                <MusicToggleButton variant="compact" className="!bg-black/60 !text-stone-200 !border-slate-700 backdrop-blur-sm shadow-xs" />
              </div>

              {/* Main Action CTA */}
              <div className="w-full max-w-md md:max-w-xl flex-1 flex flex-col justify-center items-center gap-2.5 md:gap-3.5">
                <button
                  type="button"
                  onClick={startAdventure}
                  className="w-full py-4 sm:py-5 md:py-6 bg-gradient-to-r from-emerald-900/85 via-teal-900/85 to-emerald-900/85 hover:from-emerald-800/90 hover:to-teal-800/90 backdrop-blur-md text-white font-cinzel font-black text-lg sm:text-xl md:text-2xl uppercase tracking-[0.18em] border-2 border-emerald-400 hover:border-emerald-300 shadow-[0_4px_25px_rgba(0,0,0,0.5)] active:scale-95 transition-all rounded-2xl cursor-pointer flex items-center justify-center group relative overflow-hidden text-center"
                >
                  {/* Diagonal Luminous Reflection Shimmer */}
                  <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-emerald-100/25 to-transparent pointer-events-none animate-menu-shimmer" />
                  
                  <span className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)] text-emerald-100 group-hover:text-white font-black">
                    {hasActiveSave ? "CONTINUER L'AVENTURE" : "COMMENCER L'AVENTURE"}
                  </span>
                </button>

                {/* Secondary navigation for Adventure mode with transparency */}
                <div className="grid grid-cols-2 gap-2 md:gap-3 w-full">
                  <button
                    type="button"
                    onClick={() => {
                      clickSFX();
                      setGameState('MAIN_MENU');
                    }}
                    className="py-2.5 sm:py-3 md:py-3.5 px-4 bg-red-950/75 hover:bg-red-900/85 backdrop-blur-md text-white font-cinzel font-black text-xs md:text-sm uppercase tracking-wider border-2 border-red-500/70 rounded-xl cursor-pointer flex items-center justify-center gap-1.5 active:translate-y-0.5 transition-all shadow-xs drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]"
                  >
                    <svg className="w-3 h-3 md:w-3.5 md:h-3.5 fill-current shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                      <polygon points="17,4 7,12 17,20" />
                    </svg>
                    <span>Menu</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      clickSFX();
                      setIntroStep(0);
                      setGameState('INTRO');
                    }}
                    className="py-2.5 sm:py-3 md:py-3.5 bg-gradient-to-r from-[#78350f]/80 via-[#92400e]/80 to-[#78350f]/80 hover:from-[#78350f]/95 hover:via-[#92400e]/95 hover:to-[#78350f]/95 backdrop-blur-md text-white font-cinzel font-black text-xs md:text-sm uppercase tracking-wider border-2 border-amber-400/70 hover:border-amber-300 rounded-xl cursor-pointer flex items-center justify-center gap-1.5 shadow-xs active:translate-y-0.5 transition-all drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]"
                  >
                    <span className="md:text-base">📜</span>
                    <span>Prologue</span>
                  </button>
                </div>
              </div>

              {/* Footer motto */}
              <div className="text-[10px] sm:text-[11px] md:text-xs text-amber-300 font-cinzel font-bold uppercase tracking-[0.18em] text-center drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]">
                ✦ Restaure la mémoire des mots perdus à travers le Djurdjura ✦
              </div>
            </div>
          )}
          {gameState === 'NAME_INPUT' && (
            <div className="w-full h-full flex flex-col justify-center items-center gap-3.5 md:gap-5 px-4 sm:px-10 max-w-md md:max-w-lg mx-auto pb-6 sm:pb-8 md:pb-10">
              <div className="w-full max-w-sm md:max-w-md flex items-center justify-between border-b-2 border-amber-800/20 pb-1.5 flex-shrink-0">
                <div className="text-amber-950 text-xs md:text-sm font-black uppercase tracking-widest font-cinzel">
                  Ton prénom
                </div>
                {playerName && playerName !== 'Voyageur' && (
                  <span className="text-[10px] md:text-xs text-stone-600 font-mono font-bold">
                    Actuel : <strong className="text-stone-900 font-black">{playerName}</strong>
                  </span>
                )}
              </div>
              
              <div className="w-full max-w-sm md:max-w-md flex flex-col gap-3 md:gap-4">
                <input 
                  type="text" 
                  value={tempName}
                  onChange={e => setTempName(e.target.value)}
                  placeholder="EX: MASSINISSA"
                  className="w-full bg-[#fffdf9] border-2 border-[#b45309]/40 rounded-xl p-3.5 md:p-4 text-stone-900 font-black text-center text-lg sm:text-xl md:text-2xl uppercase placeholder:text-stone-400 focus:outline-none focus:border-amber-600 focus:ring-2 focus:ring-amber-500/30 shadow-xs"
                  maxLength={20}
                  onKeyDown={(e) => { if (e.key === "Enter") submitName(); }}
                  autoFocus
                />

                <div className="flex gap-2 md:gap-3 w-full">
                  {playerName && (
                    <button type="button"
                      onClick={() => {
                        clickSFX();
                        setGameState('MAIN_MENU');
                      }}
                      className="py-3 md:py-3.5 px-3.5 md:px-5 bg-[#fff1f2] hover:bg-[#ffe4e6] text-red-900 font-black text-xs md:text-sm uppercase tracking-wider border-2 border-red-300 rounded-xl cursor-pointer transition-all shadow-xs flex items-center gap-1.5"
                    >
                      <svg className="w-3 h-3 md:w-3.5 md:h-3.5 fill-current shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                        <polygon points="17,4 7,12 17,20" />
                      </svg>
                      <span>Menu</span>
                    </button>
                  )}
                  <button type="button"
                    onClick={submitName}
                    className="flex-1 py-3 md:py-3.5 bg-gradient-to-r from-amber-500 via-amber-400 to-amber-500 hover:from-amber-400 hover:to-amber-300 text-stone-950 font-black text-sm md:text-base uppercase tracking-widest border-2 border-amber-600 shadow-xs active:translate-y-0.5 transition-all rounded-xl cursor-pointer flex items-center justify-center gap-2"
                  >
                    <span>{hasActiveSave ? 'Continuer' : 'Valider'}</span>
                    <span>▶</span>
                  </button>
                </div>

                {/* Accès discret au mot de passe Administrateur */}
                <div className="flex items-center justify-center pt-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      clickSFX();
                      setShowAdminPasswordModal(true);
                    }}
                    className="text-[11px] text-amber-900/80 hover:text-amber-950 font-bold flex items-center gap-1.5 transition-colors cursor-pointer px-3 py-1 rounded-full hover:bg-amber-100/60"
                  >
                    <span>👑</span>
                    <span>Accès Mode Administrateur</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {gameState === 'MAIN_MENU' && (
            <div className={`w-full h-full flex flex-col justify-center items-center ${
              playerName === 'Admin' ? 'gap-1.5 sm:gap-2 py-0.5' : 'gap-2 sm:gap-2.5 py-1'
            } max-w-xl md:max-w-2xl lg:max-w-3xl mx-auto relative`}>

              {/* Ambient Floating Stardust Particles & Warm Backlight */}
              <div className="absolute inset-0 pointer-events-none overflow-hidden z-0">
                {[
                  { left: '12%', delay: '0s', duration: '5.5s', size: 'w-1.5 h-1.5' },
                  { left: '28%', delay: '1.8s', duration: '6.2s', size: 'w-2 h-2' },
                  { left: '46%', delay: '0.9s', duration: '7s', size: 'w-1 h-1' },
                  { left: '64%', delay: '3.1s', duration: '5.8s', size: 'w-1.5 h-1.5' },
                  { left: '82%', delay: '1.4s', duration: '6.5s', size: 'w-2 h-2' },
                  { left: '92%', delay: '2.5s', duration: '5.2s', size: 'w-1 h-1' },
                ].map((p, idx) => (
                  <div
                    key={idx}
                    className={`absolute bottom-1 rounded-full bg-gradient-to-t from-amber-400 to-yellow-200 blur-[0.5px] opacity-75 animate-particle-drift ${p.size}`}
                    style={{
                      left: p.left,
                      animationDelay: p.delay,
                      animationDuration: p.duration,
                    }}
                  />
                ))}
                {/* Subtle warm atmospheric glow */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-44 bg-amber-500/10 blur-3xl rounded-full" />
              </div>

              {/* Row 1: Hero Play Buttons - AVENTURE & DÉFIS with Dynamic Depth & Balanced Centered Layout */}
              <div className="grid grid-cols-2 gap-2.5 sm:gap-3.5 w-full flex-shrink-0 z-10">
                {/* Button Aventure - Primary Hero Card */}
                <button type="button"
                  onClick={() => {
                    clickSFX();
                    setGameState('ADVENTURE_TITLE');
                  }}
                  className="w-full h-20 sm:h-22 md:h-24 bg-gradient-to-br from-[#854d0e] via-[#92400e] to-[#78350f] hover:from-[#92400e] hover:via-[#a16207] hover:to-[#854d0e] text-white font-bold tracking-wider border-2 border-amber-300 hover:border-yellow-200 animate-menu-glow active:scale-[0.98] active:translate-y-0.5 transition-all flex flex-col items-center justify-center rounded-2xl cursor-pointer px-2.5 sm:px-4 py-2.5 sm:py-3 group relative overflow-hidden shadow-lg"
                >
                  {/* Diagonal Light Shimmer */}
                  <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-amber-100/25 to-transparent pointer-events-none animate-menu-shimmer" />

                  {/* Title & Icon - Perfectly Centered */}
                  <div className="flex items-center justify-center gap-2 sm:gap-2.5">
                    <span className="text-amber-300 text-lg sm:text-2xl animate-gentle-float group-hover:scale-110 transition-transform drop-shadow-[0_0_8px_rgba(251,191,36,0.7)] leading-none select-none">
                      {hasActiveSave ? '▶' : '🗺️'}
                    </span>
                    <span className="font-cinzel font-black text-sm sm:text-base md:text-lg text-white uppercase drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] tracking-wider">
                      Aventure
                    </span>
                  </div>

                  {/* Subtitle - Cleanly Centered with breathing room */}
                  <span className="text-[11px] sm:text-xs md:text-sm text-amber-200/90 font-medium text-center leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)] mt-1.5 max-w-[95%]">
                    La Légende des Mots Oubliés
                  </span>
                </button>

                {/* Button Défis - Arcade & Mini-Jeux Hero Card */}
                <button type="button"
                  onClick={openChallenges}
                  className="w-full h-20 sm:h-22 md:h-24 bg-gradient-to-br from-[#831843] via-[#881337] to-[#701a75] hover:from-[#9d174d] hover:via-[#9f1239] hover:to-[#86198f] text-white font-bold tracking-wider border-2 border-amber-400/80 hover:border-amber-300 active:scale-[0.98] active:translate-y-0.5 transition-all flex flex-col items-center justify-center rounded-2xl cursor-pointer px-2.5 sm:px-4 py-2.5 sm:py-3 group relative overflow-hidden shadow-[0_4px_20px_rgba(136,19,55,0.45)]"
                >
                  {/* Diagonal Light Shimmer */}
                  <div className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-rose-100/20 to-transparent pointer-events-none animate-menu-shimmer" />

                  {/* Title & Icon - Perfectly Centered */}
                  <div className="flex items-center justify-center gap-2 sm:gap-2.5">
                    <span className="text-rose-300 text-lg sm:text-2xl group-hover:rotate-12 group-hover:scale-110 transition-transform drop-shadow-[0_0_8px_rgba(244,63,94,0.7)] leading-none select-none">
                      🎯
                    </span>
                    <span className="font-cinzel font-black text-sm sm:text-base md:text-lg text-white uppercase drop-shadow-[0_2px_4px_rgba(0,0,0,0.9)] tracking-wider">
                      Défis
                    </span>
                  </div>

                  {/* Subtitle - Cleanly Centered with breathing room */}
                  <span className="text-[11px] sm:text-xs md:text-sm text-rose-200/90 font-medium text-center leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)] mt-1.5 max-w-[95%]">
                    Quizz & Mots croisés
                  </span>
                </button>
              </div>

              {/* Row 2: Dictionnaire & Inventaire - Clean Direct Icons */}
              <div className="grid grid-cols-2 gap-2.5 sm:gap-3.5 w-full flex-shrink-0 z-10">
                <button type="button"
                  onClick={openDictionary}
                  className="w-full h-11 sm:h-12 md:h-13 bg-gradient-to-r from-stone-900/90 via-[#292524]/90 to-stone-900/90 hover:from-stone-800/95 hover:to-stone-800/95 text-white font-cinzel font-bold text-xs sm:text-sm md:text-base border border-amber-400/70 hover:border-amber-300 shadow-md active:scale-[0.98] active:translate-y-0.5 transition-all rounded-xl sm:rounded-2xl cursor-pointer flex items-center justify-center gap-2 drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)] px-3 group"
                >
                  <span className="text-base sm:text-lg md:text-xl group-hover:scale-110 transition-transform select-none leading-none">
                    📖
                  </span>
                  <span className="text-amber-100 group-hover:text-white uppercase tracking-wider">Dictionnaire</span>
                </button>

                <button type="button"
                  onClick={() => openInventory('skins')}
                  className="w-full h-11 sm:h-12 md:h-13 bg-gradient-to-r from-stone-900/90 via-[#292524]/90 to-stone-900/90 hover:from-stone-800/95 hover:to-stone-800/95 text-white font-cinzel font-bold text-xs sm:text-sm md:text-base border border-amber-400/70 hover:border-amber-300 shadow-md active:scale-[0.98] active:translate-y-0.5 transition-all rounded-xl sm:rounded-2xl cursor-pointer flex items-center justify-center gap-2 drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)] px-3 group"
                >
                  <span className="text-base sm:text-lg md:text-xl group-hover:scale-110 transition-transform select-none leading-none">
                    🎒
                  </span>
                  <span className="text-amber-100 group-hover:text-white uppercase tracking-wider">Inventaire</span>
                </button>
              </div>

              {/* Row 3: Profil & Guide Tutoriel */}
              <div className="grid grid-cols-2 gap-2 sm:gap-3 w-full flex-shrink-0 z-10">
                <button type="button"
                  onClick={() => {
                    clickSFX();
                    setTempName('');
                    setGameState('NAME_INPUT');
                  }}
                  className="w-full h-8 sm:h-9 md:h-10 bg-stone-950/70 hover:bg-stone-900/90 text-amber-200/90 hover:text-white font-cinzel font-bold text-[11px] sm:text-xs md:text-sm border border-amber-400/50 hover:border-amber-300 active:scale-95 transition-all rounded-full cursor-pointer flex items-center justify-center gap-1.5 shadow-xs px-3 truncate"
                >
                  <span className="text-xs sm:text-sm opacity-80 shrink-0">👤</span>
                  <span className="tracking-wide truncate">{playerName === 'Admin' ? 'Mode Joueur' : 'Profil'}</span>
                </button>

                <button type="button"
                  onClick={() => {
                    clickSFX();
                    setShowTrainingGuideModal(true);
                  }}
                  className="w-full h-8 sm:h-9 md:h-10 bg-gradient-to-r from-amber-950/80 via-stone-900/90 to-amber-950/80 hover:from-amber-900/90 hover:to-amber-900/90 text-amber-200 hover:text-white font-cinzel font-bold text-[11px] sm:text-xs md:text-sm border border-amber-400/60 hover:border-amber-300 active:scale-95 transition-all rounded-full cursor-pointer flex items-center justify-center gap-1.5 shadow-xs px-3 truncate"
                >
                  <span className="text-xs sm:text-sm shrink-0">💡</span>
                  <span className="tracking-wide truncate">Guide & Astuces</span>
                </button>
              </div>

              {/* Row 4: Dedicated Admin Button for Admin user */}
              {playerName === 'Admin' && (
                <div className="w-full flex-shrink-0 z-10">
                  <button type="button"
                    onClick={() => {
                      clickSFX();
                      setGameState('ADMIN_DASHBOARD');
                    }}
                    className="w-full h-8 sm:h-9 md:h-10 bg-gradient-to-r from-amber-950/80 via-stone-900/90 to-amber-950/80 hover:from-amber-900 hover:to-amber-900 text-amber-200 font-cinzel font-bold text-xs sm:text-sm border border-amber-400/80 hover:border-amber-300 shadow-xs active:scale-95 transition-all rounded-full cursor-pointer flex items-center justify-center gap-2"
                  >
                    <span className="text-xs sm:text-sm">👑</span>
                    <span className="tracking-wide">Studio & Gestion des Sauvegardes</span>
                  </button>
                </div>
              )}

              {/* Row 5: Discreet Copyright & Legal Mentions */}
              <div className="w-full flex items-center justify-center gap-2 pt-1 flex-shrink-0 z-10 text-[10px] sm:text-[11px] text-amber-200/60 font-cinzel flex-wrap">
                <span>© 2026 Yoann Baron</span>
                <span className="opacity-40">•</span>
                <button
                  type="button"
                  onClick={() => {
                    clickSFX();
                    setShowLegalRightsModal(true);
                  }}
                  className="text-amber-300/75 hover:text-amber-200 underline underline-offset-2 transition-colors cursor-pointer"
                >
                  Mentions Légales & Sources
                </button>
                {playerName !== 'Admin' && (
                  <>
                    <span className="opacity-40">•</span>
                    <button
                      type="button"
                      onClick={() => {
                        clickSFX();
                        setShowAdminPasswordModal(true);
                      }}
                      className="text-amber-300/75 hover:text-amber-200 underline underline-offset-2 transition-colors cursor-pointer flex items-center gap-1"
                      title="Accès Administrateur protégé"
                    >
                      <span>👑</span>
                      <span>Admin</span>
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {gameState === 'INTRO' && (
            <div className="w-full h-full flex flex-col justify-between gap-3 md:gap-4 max-w-2xl md:max-w-3xl lg:max-w-4xl mx-auto pb-3 sm:pb-6">
              <div 
                ref={dialogueBoxRef}
                onClick={revealFullDialogue}
                className="bg-black/85 backdrop-blur-sm border-2 border-amber-500 p-3.5 sm:p-4 md:p-5 rounded-lg md:rounded-xl flex-1 overflow-y-auto flex flex-col justify-start shadow-[inset_0_0_15px_rgba(245,158,11,0.3)] cursor-pointer"
                title="Cliquer pour afficher tout le texte"
              >
                <div className="flex items-center justify-between border-b border-amber-900 pb-1 mb-2">
                  <span className="text-amber-400 text-xs md:text-sm font-bold uppercase flex items-center gap-1.5 shrink-0">
                    <span>{currentSpeaker.icon}</span>
                    <span>{currentSpeaker.speaker}</span>
                  </span>
                </div>
                <div className="text-white font-mono text-sm sm:text-base md:text-lg leading-relaxed whitespace-pre-wrap break-words font-medium">
                  {displayedText}
                </div>
              </div>
              <div className="flex gap-2 md:gap-3 w-full flex-shrink-0">
                <button type="button"
                  onClick={() => {
                    clickSFX();
                    setGameState('MAIN_MENU');
                  }}
                  className="py-3.5 md:py-4 px-4 md:px-6 bg-red-900 hover:bg-red-800 text-white font-bold text-xs md:text-sm uppercase tracking-wider border-2 border-red-500 shadow-[2px_2px_0_red] active:translate-y-0.5 transition-all cursor-pointer shrink-0 flex items-center gap-1.5 rounded-lg"
                >
                  <svg className="w-3 h-3 md:w-3.5 md:h-3.5 fill-current shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                    <polygon points="17,4 7,12 17,20" />
                  </svg>
                  <span>Menu</span>
                </button>
                <button type="button"
                  onClick={nextIntro}
                  className="flex-1 py-3.5 md:py-4 bg-cyan-600 hover:bg-cyan-500 text-white font-bold text-sm md:text-base uppercase tracking-widest border-2 border-cyan-400 shadow-[4px_4px_0_#06b6d4] active:shadow-none active:translate-y-1 active:translate-x-1 transition-all rounded-lg cursor-pointer"
                >
                  {introStep < INTRO_DIALOGUES.length - 1 ? 'Suivant ▼' : 'Voir la Carte ▶'}
                </button>
              </div>
            </div>
          )}

          {gameState === 'REGION_INTRO' && (() => {
            const currentRegionDialogues = REGION_INTRO_DIALOGUES[currentLevelIdx] || [LEVEL_DATA[currentLevelIdx].regionIntro];
            const isLastStep = regionIntroStep >= currentRegionDialogues.length - 1;
            return (
              <div className="w-full h-full flex flex-col justify-between gap-3 md:gap-4 max-w-2xl md:max-w-3xl lg:max-w-4xl mx-auto pb-3 sm:pb-6">
                <div 
                  ref={dialogueBoxRef}
                  onClick={revealFullDialogue}
                  className="bg-black/85 backdrop-blur-sm border-2 border-emerald-500 p-3.5 sm:p-4 md:p-5 rounded-lg md:rounded-xl flex-1 overflow-y-auto flex flex-col justify-start shadow-[inset_0_0_15px_rgba(16,185,129,0.3)] cursor-pointer"
                  title="Cliquer pour afficher tout le texte"
                >
                  <span className="text-emerald-400 text-xs md:text-sm font-bold uppercase mb-2 border-b border-emerald-900 pb-1 w-max flex items-center gap-1.5 shrink-0">
                    <span>{currentSpeaker.icon}</span>
                    <span>{currentSpeaker.speaker}</span>
                  </span>
                  <div className="text-white font-mono text-sm sm:text-base md:text-lg leading-relaxed whitespace-pre-wrap break-words font-medium">
                    {displayedText}
                  </div>
                </div>
                <button type="button"
                  onClick={nextRegionIntro}
                  className="w-full py-3.5 md:py-4 bg-emerald-700 hover:bg-emerald-600 text-white font-bold text-sm md:text-base uppercase tracking-widest border-2 border-emerald-400 shadow-[4px_4px_0_#10b981] active:shadow-none active:translate-y-1 active:translate-x-1 transition-all flex-shrink-0 rounded-lg cursor-pointer"
                >
                  {isLastStep ? 'Explorer le niveau ▶' : 'Suivant ▼'}
                </button>
              </div>
            );
          })()}

          {gameState === 'BOSS_INTRO' && (() => {
            const currentBossDialogues = BOSS_INTRO_DIALOGUES[currentLevelIdx] || [LEVEL_DATA[currentLevelIdx].bossIntro];
            const isLastStep = bossIntroStep >= currentBossDialogues.length - 1;
            return (
              <div className="w-full h-full flex flex-col justify-between gap-3 md:gap-4 max-w-2xl md:max-w-3xl lg:max-w-4xl mx-auto pb-3 sm:pb-6">
                <div 
                  ref={dialogueBoxRef}
                  onClick={revealFullDialogue}
                  className="bg-black/85 backdrop-blur-sm border-2 border-red-500 p-3.5 sm:p-4 md:p-5 rounded-lg md:rounded-xl flex-1 overflow-y-auto flex flex-col justify-start shadow-[inset_0_0_15px_rgba(239,68,68,0.3)] cursor-pointer"
                  title="Cliquer pour afficher tout le texte"
                >
                  <span className="text-red-400 text-xs md:text-sm font-bold uppercase mb-2 border-b border-red-900 pb-1 w-max flex items-center gap-1.5 shrink-0">
                    <span>{currentSpeaker.icon}</span>
                    <span>{currentSpeaker.speaker}</span>
                  </span>
                  <div className="text-white font-mono text-sm sm:text-base md:text-lg leading-relaxed whitespace-pre-wrap break-words font-medium">
                    {displayedText}
                  </div>
                </div>
                <button type="button"
                  onClick={nextBossIntro}
                  className="w-full py-3.5 md:py-4 bg-red-700 hover:bg-red-600 text-white font-bold text-sm md:text-base uppercase tracking-widest border-2 border-red-400 shadow-[4px_4px_0_#ef4444] active:shadow-none active:translate-y-1 active:translate-x-1 transition-all flex-shrink-0 rounded-lg cursor-pointer"
                >
                  {isLastStep ? 'Affronter le Gardien ⚔️' : 'Suivant ▼'}
                </button>
              </div>
            );
          })()}

          {(gameState === 'NPC_DIALOGUE' || gameState === 'EPILOGUE') && (() => {
            const isEpilogue = gameState === 'EPILOGUE';
            let isLastStep = false;
            if (isEpilogue) {
              isLastStep = epilogueStep >= EPILOGUE_DIALOGUES.length - 1;
            } else if (currentThemes[step]) {
              const list = currentThemes[step].npc.dialogues || (currentThemes[step].npc.dialogue ? [currentThemes[step].npc.dialogue] : []);
              isLastStep = npcDialogueStep >= list.length - 1;
            } else if (currentLevelData.npcEncounter && step === currentLevelData.npcEncounter.step) {
              const list = currentLevelData.npcEncounter.dialogues || [];
              isLastStep = npcDialogueStep >= list.length - 1;
            } else {
              isLastStep = true;
            }

            const borderColor = isEpilogue ? 'border-blue-400 shadow-[inset_0_0_15px_rgba(96,165,250,0.3)]' : currentThemes[step] ? 'border-amber-400 shadow-[inset_0_0_15px_rgba(251,191,36,0.3)]' : 'border-cyan-400 shadow-[inset_0_0_15px_rgba(6,182,212,0.3)]';
            const textColor = isEpilogue ? 'text-blue-300 border-blue-900' : currentThemes[step] ? 'text-amber-300 border-amber-900' : 'text-cyan-300 border-cyan-900';

            return (
              <div className="w-full h-full flex flex-col justify-between gap-2.5 md:gap-4 max-w-2xl md:max-w-3xl lg:max-w-4xl mx-auto pb-3 sm:pb-6">
                {isEpilogue && (
                  <div className="w-full bg-blue-950/80 border border-blue-400 px-3 py-1.5 md:py-2 rounded-lg flex items-center justify-between gap-2 shadow-sm shrink-0">
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-blue-400 text-sm md:text-base">💾</span>
                      <span className="text-blue-200 font-bold text-xs md:text-sm uppercase tracking-wider">
                        Victoire Finale Sauvegardée !
                      </span>
                    </div>
                    <span className="text-[11px] md:text-xs text-blue-300 font-semibold truncate">
                      5 / 5 Régions Libérées
                    </span>
                  </div>
                )}
                <div
                  ref={dialogueBoxRef}
                  onClick={revealFullDialogue}
                  className={`bg-black/85 backdrop-blur-sm border-2 ${borderColor} p-3 sm:p-3.5 md:p-5 rounded-lg md:rounded-xl flex-1 overflow-y-auto flex flex-col justify-start cursor-pointer`}
                  title="Cliquer pour afficher tout le texte"
                >
                  <span className={`${textColor} text-xs md:text-sm font-bold uppercase mb-1.5 border-b pb-1 w-max flex items-center gap-1.5 shrink-0`}>
                    <span>{currentSpeaker.icon}</span>
                    <span>{currentSpeaker.speaker}</span>
                  </span>
                  <div className="text-white font-mono text-sm sm:text-base md:text-lg leading-relaxed whitespace-pre-wrap break-words font-medium">
                    {displayedText}
                  </div>
                </div>
                {isEpilogue && isLastStep ? (
                  <div className="flex flex-col sm:flex-row gap-2 md:gap-3 w-full">
                    <button type="button"
                      onClick={() => {
                        clickSFX();
                        setGameState('CELESTIAL_CHALLENGE');
                      }}
                      className="flex-1 py-3.5 sm:py-4 md:py-5 bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 hover:from-amber-400 hover:to-yellow-300 text-slate-950 font-black text-xs sm:text-sm md:text-base uppercase tracking-wider rounded-lg shadow-[0_0_20px_rgba(251,191,36,0.6)] active:translate-y-0.5 transition-all flex items-center justify-center gap-2 cursor-pointer border-2 border-amber-200"
                    >
                      <span>🚀</span>
                      <span>Grimper vers la Lune (50 Quizz)</span>
                    </button>
                    <button type="button"
                      onClick={() => {
                        clickSFX();
                        setGameState('WORLD_MAP');
                      }}
                      className="py-3 px-4 md:px-6 bg-slate-900 hover:bg-slate-800 text-amber-300 font-bold text-xs md:text-sm uppercase tracking-wider border-2 border-amber-500/50 rounded-lg active:translate-y-0.5 transition-all flex items-center justify-center gap-1 cursor-pointer shrink-0 shadow-[2px_2px_0_#b45309]"
                    >
                      <span>🗺️</span>
                      <span>Carte</span>
                    </button>
                  </div>
                ) : (
                  <button type="button"
                    onClick={isEpilogue ? nextEpilogue : nextNpcDialogue}
                    className="w-full py-3.5 sm:py-4 md:py-5 bg-amber-600 hover:bg-amber-500 text-white font-bold text-sm md:text-base uppercase tracking-widest border-2 border-amber-300 shadow-[4px_4px_0_#f59e0b] active:shadow-none active:translate-y-1 active:translate-x-1 transition-all flex-shrink-0 rounded-lg cursor-pointer"
                  >
                    {isEpilogue
                      ? (isLastStep ? 'Terminer le Voyage 🏆' : 'Suivant ▼')
                      : (isLastStep ? 'Relever le défi ▶' : 'Suivant ▼')}
                  </button>
                )}
              </div>
            );
          })()}

          {gameState === 'REVISION' && (
            <div className="w-full h-full flex flex-col justify-between gap-3 md:gap-4 max-w-xl md:max-w-2xl mx-auto pb-3 sm:pb-6">
              <div className="bg-black/85 backdrop-blur-sm border-2 border-orange-500 p-4 md:p-6 rounded-lg md:rounded-xl flex-1 overflow-hidden flex flex-col justify-center items-center text-center shadow-[inset_0_0_15px_rgba(249,115,22,0.3)]">
                <h3 className="text-orange-400 uppercase font-bold text-lg sm:text-xl md:text-2xl mb-2">
                  Phase de Révision
                </h3>
                <div className="text-slate-300 text-xs sm:text-sm md:text-base mb-2 leading-relaxed max-w-md">
                  Tu as raté certains mots sur le chemin. Le Gardien exige que tu les
                  maîtrises avant de t'affronter.
                </div>
                <div className="text-orange-400 text-xs sm:text-sm md:text-base font-bold">
                  Reste : {failedCount} mot(s)
                </div>
              </div>
              <button type="button"
                onClick={() => {
                  clickSFX();
                  setGameState('REVISION_PLAY');
                  generateQuestion(
                    false,
                    currentLevelIdx,
                    step,
                    true,
                    failedCurrentRunRef.current
                  );
                }}
                className="w-full py-3.5 md:py-4.5 bg-amber-600 hover:bg-amber-500 text-white font-bold text-sm md:text-base uppercase tracking-widest border-2 border-amber-400 shadow-[4px_4px_0_#f59e0b] active:shadow-none active:translate-y-1 active:translate-x-1 transition-all flex-shrink-0 rounded-lg cursor-pointer"
              >
                Réparer mes erreurs
              </button>
            </div>
          )}

          {gameState === 'WORLD_MAP' && (
            <div className="w-full h-full flex flex-col justify-between gap-2.5 md:gap-3.5 pb-6 sm:pb-8 md:pb-10 max-w-2xl md:max-w-3xl lg:max-w-4xl mx-auto relative z-10">
                <div className="flex w-full items-center justify-between px-2 sm:px-3 md:px-5 py-2 md:py-3 flex-1 border-2 border-amber-500 rounded-lg md:rounded-xl bg-black/85 min-h-[95px] sm:min-h-[110px] md:min-h-[130px] shadow-[inset_0_0_15px_rgba(245,158,11,0.3)] relative overflow-hidden">
                  {/* Previous Node Arrow Button */}
                  <button type="button"
                    onClick={() => {
                      clickSFX();
                      setSelectedMapNode((prev) => (prev > 0 ? prev - 1 : 5));
                    }}
                    className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-slate-800 hover:bg-slate-700 text-amber-300 flex items-center justify-center text-sm sm:text-base md:text-lg font-bold active:scale-90 transition-all cursor-pointer shrink-0 border border-slate-600 shadow-sm"
                    aria-label="Région précédente"
                  >
                    <svg className="w-4 h-4 fill-current shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                      <polygon points="17,4 7,12 17,20" />
                    </svg>
                  </button>

                  {/* Central World Info */}
                  <div className="flex-1 flex flex-col justify-center items-center text-center px-2 py-0.5 min-w-0">
                    {selectedMapNode === 5 ? (
                      <>
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-[10px] sm:text-xs md:text-sm px-2 py-0.5 bg-yellow-950 text-yellow-300 border border-yellow-500 rounded-full font-mono font-bold uppercase tracking-wider">
                            Étape Finale
                          </span>
                          <span className="text-sm sm:text-base md:text-lg">🌙</span>
                        </div>
                        <h3 className="text-yellow-300 uppercase font-bold text-base sm:text-lg md:text-2xl tracking-wider leading-tight drop-shadow truncate max-w-full">
                          Ayur (La Lune)
                        </h3>
                        <div className="text-slate-300 text-xs sm:text-sm md:text-base font-medium mb-1 truncate max-w-full">
                          L'Ultime Défi Céleste
                        </div>
                        <div className="flex flex-wrap justify-center items-center gap-1 text-[10px] sm:text-[11px] md:text-xs text-slate-300">
                          <span className="inline-flex items-center gap-1 bg-slate-900 px-2 py-0.5 rounded border border-slate-700 text-slate-300">
                            <span>🌌</span>
                            <span>Ascension Céleste</span>
                          </span>
                          <span className="inline-flex items-center gap-1 bg-slate-900 px-2 py-0.5 rounded border border-slate-700 text-slate-300">
                            <span>📜</span>
                            <span>Amawal Complet</span>
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-[10px] sm:text-xs md:text-sm px-2 py-0.5 bg-amber-950 text-amber-300 border border-amber-500 rounded-full font-mono font-bold uppercase tracking-wider">
                            Étape {selectedMapNode + 1}/5
                          </span>
                          <span className="text-sm sm:text-base md:text-lg">{LEVEL_DATA[selectedMapNode].steleIcon}</span>
                        </div>
                        <h3 className="text-amber-400 uppercase font-bold text-base sm:text-lg md:text-2xl tracking-wider leading-tight drop-shadow truncate max-w-full">
                          {LEVEL_DATA[selectedMapNode].name}
                        </h3>
                        <div className="text-slate-300 text-xs sm:text-sm md:text-base font-medium mb-1 truncate max-w-full">
                          {LEVEL_DATA[selectedMapNode].subtitle}
                        </div>
                        <div className="flex flex-wrap justify-center items-center gap-1 text-[10px] sm:text-[11px] md:text-xs text-slate-300 max-w-full">
                          <span 
                            className="inline-flex items-center gap-1 bg-slate-900 px-2 py-0.5 rounded border border-slate-700 text-slate-300 truncate max-w-[200px] sm:max-w-[280px] md:max-w-[400px]" 
                            title={LEVEL_DATA[selectedMapNode].scroll?.title}
                          >
                            <span>📜</span>
                            <span className="truncate">{LEVEL_DATA[selectedMapNode].scroll?.title?.split(' (')[0]}</span>
                          </span>
                          <span className="inline-flex items-center gap-1 bg-slate-900 px-2 py-0.5 rounded border border-slate-700 text-slate-300 shrink-0">
                            <span>⭐</span>
                            <span>3 Épreuves</span>
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Next Node Arrow Button */}
                  <button type="button"
                    onClick={() => {
                      clickSFX();
                      setSelectedMapNode((prev) => (prev < 5 ? prev + 1 : 0));
                    }}
                    className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full bg-slate-800 hover:bg-slate-700 text-amber-300 flex items-center justify-center text-sm sm:text-base md:text-lg font-bold active:scale-90 transition-all cursor-pointer shrink-0 border border-slate-600 shadow-sm"
                    aria-label="Région suivante"
                  >
                    <svg className="w-4 h-4 fill-current shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                      <polygon points="7,4 17,12 7,20" />
                    </svg>
                  </button>
                </div>

                {/* Action Buttons Row */}
                <div className="flex gap-2 md:gap-3 w-full flex-shrink-0">
                  <button type="button"
                    onClick={() => {
                      clickSFX();
                      setGameState('MAIN_MENU');
                    }}
                    className="py-2.5 sm:py-3 md:py-3.5 px-3 md:px-5 bg-red-900 hover:bg-red-800 text-white font-bold text-xs md:text-sm uppercase tracking-wider border-2 border-red-500 shadow-[2px_2px_0_red] active:translate-y-0.5 transition-all flex items-center justify-center gap-1.5 cursor-pointer shrink-0 rounded-lg"
                  >
                    <svg className="w-3 h-3 md:w-3.5 md:h-3.5 fill-current shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                      <polygon points="17,4 7,12 17,20" />
                    </svg>
                    <span>Menu</span>
                  </button>
                  <button type="button"
                    onClick={() => {
                      if (selectedMapNode === 5) {
                        clickSFX();
                        setGameState('CELESTIAL_CHALLENGE');
                      } else if (isAdmin || unlockedLevels.includes(selectedMapNode)) {
                        startLevel(selectedMapNode);
                      }
                    }}
                    disabled={!isAdmin && !unlockedLevels.includes(selectedMapNode)}
                    className={`flex-1 py-2.5 sm:py-3 md:py-3.5 font-bold text-xs sm:text-sm md:text-base uppercase tracking-wider border-2 rounded-lg transition-all ${
                      (isAdmin || unlockedLevels.includes(selectedMapNode))
                        ? 'bg-gradient-to-r from-amber-600 to-yellow-500 hover:from-amber-500 hover:to-yellow-400 text-black border-amber-300 shadow-[4px_4px_0_#f59e0b] active:translate-y-0.5 cursor-pointer'
                        : 'bg-slate-900 text-slate-500 border-slate-700 cursor-not-allowed opacity-60'
                    }`}
                  >
                    {selectedMapNode === 5 
                      ? ((isAdmin || unlockedLevels.includes(5)) ? 'Lancer le Défi 🌙' : 'Verrouillé 🔒') 
                      : (isAdmin || unlockedLevels.includes(selectedMapNode) ? (isAdmin && !unlockedLevels.includes(selectedMapNode) ? 'Voyager (Débloqué) ➔' : 'Voyager ➔') : 'Verrouillé 🔒')}
                  </button>
                </div>
              </div>
          )}

          {(gameState === 'RUN' ||
            gameState === 'BOSS' ||
            gameState === 'REVISION_PLAY') &&
            currentQuestion && (
              <div className="w-full h-full flex flex-col gap-2.5 md:gap-3.5 pb-4 sm:pb-6 md:pb-8 max-w-2xl md:max-w-3xl lg:max-w-4xl mx-auto">
                <div
                  className="bg-black/92 sm:bg-black/95 backdrop-blur-md border border-slate-700/80 p-4 md:p-5 flex-shrink-0 flex flex-col justify-center overflow-y-auto min-h-[35%] relative rounded-lg md:rounded-xl shadow-2xl"
                >
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2">
                      <div
                        className={`text-[10px] md:text-xs uppercase font-bold tracking-wider ${
                          gameState === 'REVISION_PLAY'
                            ? 'text-orange-400'
                            : 'text-slate-400'
                        }`}
                      >
                        {gameState === 'REVISION_PLAY'
                          ? 'Révision'
                          : currentQuestion.type}
                      </div>
                    </div>
                    {gameState === 'RUN' && !currentThemes[step] && (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] md:text-xs font-bold text-slate-400 uppercase tracking-widest">
                          Stèle
                        </span>
                        <div className="w-16 md:w-24 h-1.5 md:h-2 bg-black/50 rounded-full border border-slate-700 overflow-hidden">
                          <div
                            className="h-full bg-cyan-400 transition-all duration-300"
                            style={{ width: `${(step / 10) * 100}%` }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-3 sm:gap-4.5">
                    {currentThemes[step] && getWordIcon(currentQuestion) && (
                      <div className="w-12 h-12 sm:w-16 sm:h-16 shrink-0 bg-slate-900/80 border-2 border-amber-300/60 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-[0_0_15px_rgba(251,191,36,0.25)]">
                        <span className="text-3xl sm:text-4xl md:text-5xl drop-shadow-md select-none">{getWordIcon(currentQuestion)}</span>
                      </div>
                    )}
                    <div className="flex-1 text-white text-base sm:text-lg md:text-xl whitespace-normal break-words leading-snug">
                      Traduis :{' '}
                      <span
                        className={`${
                          gameState === 'BOSS'
                            ? 'text-red-400'
                            : gameState === 'REVISION_PLAY'
                            ? 'text-orange-400'
                            : currentThemes[step]
                            ? 'text-amber-400'
                            : 'text-cyan-400'
                        } font-black text-xl sm:text-2xl md:text-3xl`}
                      >
                        « {formatPromptWithGrammar(currentQuestion, isFrToKabQuestion)} »
                      </span>
                      {!isFrToKabQuestion && currentQuestion && (
                        <TTSButton text={currentQuestion.kab} className="ml-2 bg-black/40 border border-transparent" />
                      )}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3 md:gap-4 flex-1">
                  {options.map((opt, i) => {
                    const isFailed = failedOptionKabs.includes(opt.kab);
                    const isCorrect = currentQuestion && opt.kab === currentQuestion.kab;
                    const showFeedback = !!feedback;

                    const hasTTS = (difficulty === 'CONFIRME' || (currentThemes[step] && currentQuestion && getWordIcon(currentQuestion)));
                    const optText = stripGrammarParentheses(hasTTS ? opt.kab : opt.fr);
                    const optLen = optText.length;
                    const fontSizeClass =
                      optLen > 30
                        ? 'text-[11px] sm:text-xs md:text-sm leading-tight'
                        : optLen > 18
                        ? 'text-xs sm:text-sm md:text-base leading-snug'
                        : 'text-sm sm:text-base md:text-lg leading-tight';

                    let dynamicBg = 'bg-black/90 hover:bg-black/98 backdrop-blur-md text-white border-2 border-slate-700/80 hover:border-cyan-400 active:scale-[0.98] cursor-pointer shadow-lg';

                    if (showFeedback) {
                      if (isCorrect) {
                        dynamicBg = 'bg-emerald-950/95 text-emerald-200 border-2 border-emerald-400 shadow-[0_0_20px_rgba(16,185,129,0.7)] scale-[1.02]';
                      } else if (isFailed) {
                        dynamicBg = 'bg-red-950/80 text-red-300/80 border-2 border-red-700 line-through opacity-60 cursor-not-allowed';
                      } else {
                        dynamicBg = 'bg-black/60 text-slate-500 border-2 border-slate-800 opacity-40';
                      }
                    } else if (isFailed) {
                      dynamicBg = 'bg-red-950/70 text-red-400/60 border-2 border-red-900/70 line-through opacity-50 cursor-not-allowed pointer-events-none shadow-none';
                    }

                    return (
                      <div
                        role="button"
                        tabIndex={isFailed || showFeedback ? -1 : 0}
                        key={i}
                        onClick={() => !isFailed && !showFeedback && handleAnswer(opt)}
                        onKeyDown={(e) => { if (e.key === "Enter" && !isFailed && !showFeedback) handleAnswer(opt); }}
                        className={`font-bold ${fontSizeClass} uppercase tracking-wider flex flex-col items-center justify-center text-center p-2.5 sm:p-3 md:p-4 whitespace-normal break-words transition-all rounded-lg md:rounded-xl relative ${hasTTS ? 'pr-7 sm:pr-8' : ''} ${dynamicBg}`}
                      >
                        <span className="max-w-full px-1 break-words">
                          {optText}
                        </span>
                        {showFeedback && isCorrect && (
                          <span className="text-[10px] font-black uppercase text-emerald-300 bg-emerald-900/90 px-2 py-0.5 rounded mt-1 border border-emerald-500">
                            ✓ Bonne réponse
                          </span>
                        )}
                        {hasTTS && (
                          <div className="absolute right-1 top-1/2 -translate-y-1/2" onClick={e => e.stopPropagation()}>
                            <TTSButton text={opt.kab} className="hover:bg-slate-700 border-transparent" />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          {gameState === 'GAME_OVER' && (
            <div className="w-full h-full flex flex-col justify-center gap-3 md:gap-4 max-w-md md:max-w-lg mx-auto pb-4 sm:pb-8">
              {isAdmin && (
                <button type="button"
                  onClick={adminRevive}
                  className="w-full py-3.5 md:py-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-sm md:text-base uppercase tracking-widest border-2 border-emerald-400 shadow-[4px_4px_0_#10b981] active:shadow-none active:translate-y-1 transition-all rounded-lg cursor-pointer flex items-center justify-center gap-2"
                >
                  <span>✨</span>
                  <span>Ressusciter & Reprendre</span>
                </button>
              )}
              <button type="button"
                onClick={() => {
                  clickSFX();
                  setGameState('WORLD_MAP');
                }}
                className="w-full py-4 md:py-4.5 bg-red-800 hover:bg-red-700 text-white font-bold text-sm md:text-base uppercase tracking-widest border-2 border-red-500 shadow-[4px_4px_0_#ef4444] active:shadow-none active:translate-y-1 active:translate-x-1 transition-all rounded-lg cursor-pointer"
              >
                Retour à la Carte
              </button>
              <button type="button"
                onClick={() => {
                  clickSFX();
                  setGameState('MAIN_MENU');
                }}
                className="w-full py-3.5 md:py-4 bg-slate-800 hover:bg-slate-700 text-slate-200 font-bold text-xs md:text-sm uppercase tracking-widest border-2 border-slate-600 shadow-[4px_4px_0_#64748b] active:shadow-none active:translate-y-1 active:translate-x-1 transition-all rounded-lg cursor-pointer"
              >
                🏠 Menu Principal
              </button>
            </div>
          )}

          {gameState === 'VICTORY' && (() => {
            const scrollTitle = LEVEL_DATA[currentLevelIdx].scroll?.title || '';
            const vCount = 3 + (scrollTitle ? 1 : 0);
            const isLastStep = victoryStep >= vCount - 1;

            return (
              <div className="w-full h-full flex flex-col justify-between gap-2.5 md:gap-4 max-w-2xl md:max-w-3xl lg:max-w-4xl mx-auto pb-3 sm:pb-6">
                {/* Save Confirmation Notification */}
                <div className="w-full bg-emerald-950/80 border border-emerald-400 px-3 md:px-4 py-1.5 md:py-2 rounded-lg flex items-center justify-between gap-2 shadow-sm shrink-0">
                  <div className="flex items-center gap-2">
                    <span className="text-emerald-400 text-sm md:text-base">💾</span>
                    <span className="text-emerald-200 font-bold text-xs md:text-sm uppercase tracking-wider">
                      Progression Sauvegardée !
                    </span>
                  </div>
                  <span className="text-[11px] md:text-xs text-emerald-300 font-semibold truncate">
                    Niveau {currentLevelIdx + 1} validé ({currentLevelData.name})
                  </span>
                </div>

                <div 
                  ref={dialogueBoxRef}
                  onClick={revealFullDialogue}
                  className="bg-black/85 backdrop-blur-sm border-2 border-amber-500 p-3 sm:p-3.5 md:p-5 rounded-lg md:rounded-xl flex-1 overflow-y-auto flex flex-col justify-start shadow-[inset_0_0_15px_rgba(245,158,11,0.3)] cursor-pointer"
                  title="Cliquer pour afficher tout le texte"
                >
                  <span className="text-amber-400 text-xs md:text-sm font-bold uppercase mb-1.5 border-b border-amber-900 pb-1 w-max flex items-center gap-1.5 shrink-0">
                    <span>{currentSpeaker.icon}</span>
                    <span>{currentSpeaker.speaker}</span>
                  </span>
                  <div className="text-white font-mono text-sm sm:text-base md:text-lg leading-relaxed whitespace-pre-wrap break-words font-medium">
                    {displayedText}
                  </div>
                </div>

                {/* Actions row */}
                <div className="flex gap-2.5 md:gap-3 w-full flex-shrink-0 h-11 md:h-13">
                  {currentLevelData.scroll && (
                    <button type="button"
                      onClick={() => {
                        clickSFX();
                        setReadingScroll(currentLevelData.scroll);
                      }}
                      className="h-full px-3.5 md:px-5 bg-amber-600/90 hover:bg-amber-500 text-white font-bold text-xs md:text-sm uppercase tracking-wider border-2 border-amber-400 active:shadow-none active:translate-y-0.5 transition-all cursor-pointer flex items-center justify-center gap-1.5 rounded-lg shrink-0"
                    >
                      <span className="md:text-base">📖</span>
                      <span>Lire le Conte</span>
                    </button>
                  )}

                  <button type="button"
                    onClick={nextVictory}
                    className="flex-1 h-full py-2 md:py-3 bg-cyan-900/85 hover:bg-cyan-800 text-white font-bold text-xs sm:text-sm md:text-base uppercase tracking-widest border-2 border-cyan-400 shadow-[4px_4px_0_#06b6d4] active:shadow-none active:translate-y-0.5 transition-all rounded-lg cursor-pointer flex items-center justify-center gap-1.5"
                  >
                    <span>{isLastStep ? 'Continuer le Voyage' : 'Suivant'}</span>
                    <span>{isLastStep ? '▶' : '▼'}</span>
                  </button>
                </div>
              </div>
            );
          })()}
        </div>

        {/* ========================================================= */}
        {/* POPUP COMPACT DE VICTOIRE DE DÉFI (DANS L'ÉCRAN DU BAS)   */}
        {/* ========================================================= */}
        {challengeVictory && gameState !== 'VICTORY' && gameState !== 'EPILOGUE' && (
          <div className="absolute inset-0 z-50 bg-black/75 backdrop-blur-[2px] flex items-center justify-center p-3 select-none animate-in fade-in duration-150">
            <div className="bg-slate-900 border-2 border-amber-500 rounded-xl p-3.5 max-w-xs w-full text-center shadow-[0_0_20px_rgba(0,0,0,0.8)] flex flex-col items-center gap-2.5 animate-in zoom-in-95 duration-150">
              <div className="flex items-center gap-2 w-full justify-center">
                <div className="w-8 h-8 rounded-full bg-amber-950/80 border border-amber-500 flex items-center justify-center text-base shrink-0">
                  🏆
                </div>
                <div className="text-left">
                  <h3 className="text-xs font-bold text-amber-300 uppercase tracking-wider leading-none">
                    Défi Accompli !
                  </h3>
                  <div className="text-[11px] text-amber-400/90 font-semibold uppercase tracking-wide leading-tight mt-0.5">
                    {challengeVictory.themeName}
                  </div>
                </div>
              </div>

              {/* Quick Rewards Badges Row */}
              <div className="flex items-center justify-center gap-2 w-full">
                {challengeVictory.heartGained ? (
                  <div className="flex-1 bg-emerald-950/80 border border-emerald-500/60 px-2 py-1 rounded-lg text-[11px] font-bold text-emerald-300 flex items-center justify-center gap-1">
                    <span className="animate-pulse">❤️</span>
                    <span>+1 Cœur</span>
                  </div>
                ) : (
                  <div className="flex-1 bg-slate-800/80 border border-slate-600 px-2 py-1 rounded-lg text-[10px] font-bold text-slate-300 flex items-center justify-center gap-1">
                    <span>💖</span>
                    <span>Santé Max</span>
                  </div>
                )}
                <div className="flex-1 bg-amber-950/80 border border-amber-500/60 px-2 py-1 rounded-lg text-[11px] font-bold text-amber-300 flex items-center justify-center gap-1">
                  <span>⚡</span>
                  <span>+{challengeVictory.xpGained} XP</span>
                </div>
              </div>

              {/* Compact Continue Button */}
              <button type="button"
                onClick={continueAfterChallengeVictory}
                className="w-full py-2.5 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 text-slate-950 font-black text-xs uppercase tracking-widest rounded-lg shadow-md active:translate-y-0.5 transition-all cursor-pointer flex items-center justify-center gap-1.5"
              >
                <span>Continuer l'aventure</span>
                <span>➔</span>
              </button>
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* BARRE D'ADMIN / TRICHE (ÉCRAN DU BAS - SANS REDONDANCE)   */}
        {/* ========================================================= */}
        {isAdmin && (
          <div className="w-full bg-slate-950/95 border-t border-amber-500/70 px-2 py-1 pb-[max(env(safe-area-inset-bottom),6px)] flex items-center justify-between sm:justify-center gap-1 sm:gap-2 shrink-0 z-40 text-amber-300 shadow-[0_-2px_12px_rgba(0,0,0,0.6)] backdrop-blur-sm select-none min-h-[40px]">
            {/* Step navigation */}
            <div className="flex items-center gap-1 shrink-0">
              <button type="button"
                onClick={() => adminJumpToStep(step - 1)}
                disabled={step <= 0}
                className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[11px] sm:text-xs font-black transition-all cursor-pointer ${
                  step <= 0
                    ? 'text-slate-600 opacity-40 cursor-not-allowed'
                    : 'text-amber-300 bg-amber-950/80 hover:bg-amber-900 border border-amber-500/70 active:scale-90 shadow-sm'
                }`}
                title="Étape précédente"
              >
                <svg className="w-2.5 h-2.5 fill-current inline-block shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                  <polygon points="17,4 7,12 17,20" />
                </svg>
              </button>
              <span className="text-[10px] sm:text-xs text-amber-300 font-black uppercase tracking-wider whitespace-nowrap px-0.5">
                <span className="hidden sm:inline">✨ Étape </span>
                <span className="sm:hidden">✨ </span>
                {step}/10
              </span>
              <button type="button"
                onClick={() => adminJumpToStep(step + 1)}
                disabled={step >= 10}
                className={`px-1.5 sm:px-2 py-0.5 sm:py-1 rounded text-[11px] sm:text-xs font-black transition-all cursor-pointer ${
                  step >= 10
                    ? 'text-slate-600 opacity-40 cursor-not-allowed'
                    : 'text-amber-300 bg-amber-950/80 hover:bg-amber-900 border border-amber-500/70 active:scale-90 shadow-sm'
                }`}
                title="Étape suivante"
              >
                <svg className="w-2.5 h-2.5 fill-current inline-block shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                  <polygon points="7,4 17,12 7,20" />
                </svg>
              </button>
            </div>

            {/* Level Switcher (N1 à N6 Céleste) - Fixed row, non scrollable */}
            <div className="flex items-center gap-0.5 sm:gap-1 border-l border-amber-500/40 pl-1.5 sm:pl-2 shrink-0">
              {[0, 1, 2, 3, 4, 5].map((lvlIdx) => {
                const isCelestial = lvlIdx === 5;
                const isSelected = (isCelestial && gameState === 'CELESTIAL_CHALLENGE') || (!isCelestial && gameState !== 'CELESTIAL_CHALLENGE' && currentLevelIdx === lvlIdx);

                return (
                  <button type="button"
                    key={lvlIdx}
                    onClick={() => adminSwitchLevel(lvlIdx)}
                    className={`text-[10px] sm:text-xs font-black px-1.5 sm:px-2 py-0.5 sm:py-1 rounded border transition-all cursor-pointer flex items-center justify-center ${
                      isSelected
                        ? isCelestial
                          ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white border-indigo-200 shadow-[0_0_10px_rgba(99,102,241,0.9)] scale-105'
                          : 'bg-amber-400 text-amber-950 border-amber-200 shadow-[0_0_8px_rgba(251,191,36,0.8)] scale-105'
                        : isCelestial
                        ? 'bg-indigo-950/90 text-indigo-200 border-indigo-500/60 hover:bg-indigo-900 hover:text-white'
                        : 'bg-slate-900 text-slate-300 border-slate-700 hover:bg-slate-800 hover:text-amber-300'
                    }`}
                    title={isCelestial ? 'Niveau 6 : Niveau Céleste (Ayur)' : `Niveau ${lvlIdx + 1} (${LEVEL_DATA[lvlIdx].name})`}
                  >
                    {`N${lvlIdx + 1}`}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ========================================================= */}
      {/* 4. LECTURE DU CONTE KABYLE (DOUBLE ÉCRAN PLEIN FORMAT)     */}
      {/* ========================================================= */}
      {readingScroll && (
        <ScrollDualScreenReader
          scroll={readingScroll}
          onClose={() => setReadingScroll(null)}
          sanctuaryName={currentLevelData?.name}
          isAdmin={isAdmin}
        />
      )}

      {/* Cinematic Transition Overlay */}
      <AnimatePresence>
        {isTransitioning && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.0, ease: "easeInOut" }}
            className="fixed inset-0 z-[99999] bg-slate-950 flex flex-col items-center justify-center pointer-events-auto"
          >
            <motion.div 
              initial={{ scale: 0.8, opacity: 0, rotate: -15 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              transition={{ delay: 0.2, duration: 1, type: "spring" }}
              className="text-amber-500 text-7xl font-black mb-4 drop-shadow-[0_0_20px_rgba(245,158,11,0.6)]"
            >
              ⵣ
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="text-amber-200/50 uppercase tracking-[0.3em] text-xs font-bold"
            >
              Voyage en cours...
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Schematic Gesture Coaching Overlay: Admin Title Screen */}
      <AnimatePresence>
        {showAdminTitleOverlay && isAdmin && (gameState === 'TITLE' || gameState === 'MAIN_MENU') && (
          <motion.div
            key="admin-title-coaching-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismissAdminTitleOverlay}
            className="fixed inset-0 z-[99990] flex items-center justify-center p-4 bg-black/65 backdrop-blur-xs cursor-pointer select-none"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.84, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.88, y: -6 }}
              transition={{ type: 'spring', stiffness: 380, damping: 26 }}
              className="bg-stone-950/90 backdrop-blur-md text-white border-2 border-amber-500/50 shadow-2xl rounded-2xl p-5 sm:p-6 flex flex-col items-center justify-center gap-3 max-w-[290px] sm:max-w-[340px] text-center transition-transform active:scale-95"
            >
              {/* Animated Schematic Visual: Crown + Book & Mic */}
              <div className="relative w-16 h-16 flex items-center justify-center select-none">
                {/* Glowing Golden Aura */}
                <motion.div
                  animate={{ scale: [1, 1.25, 1], opacity: [0.3, 0.7, 0.3] }}
                  transition={{ repeat: Infinity, duration: 1.8, ease: 'easeInOut' }}
                  className="absolute inset-0 bg-amber-500/30 rounded-full blur-md"
                />

                {/* Crown Emblem */}
                <motion.div
                  animate={{ y: [-3, 3, -3] }}
                  transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
                  className="relative z-10 p-3 bg-gradient-to-br from-amber-400 via-amber-600 to-yellow-500 rounded-2xl shadow-lg border border-amber-300/70 flex items-center justify-center text-3xl"
                >
                  <span>👑</span>
                </motion.div>

                {/* Floating Book & Microphone Badges */}
                <motion.span
                  animate={{ scale: [0.85, 1.15, 0.85], opacity: [0.6, 1, 0.6] }}
                  transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
                  className="absolute -top-1.5 -right-1.5 text-base z-20 pointer-events-none"
                >
                  📖
                </motion.span>
                <motion.span
                  animate={{ scale: [1.15, 0.85, 1.15], opacity: [0.6, 1, 0.6] }}
                  transition={{ repeat: Infinity, duration: 1.7, ease: 'easeInOut' }}
                  className="absolute -bottom-1 -left-1.5 text-base z-20 pointer-events-none"
                >
                  🎙️
                </motion.span>
              </div>

              {/* Text Instructions */}
              <div className="flex flex-col gap-1">
                <span className="text-sm sm:text-base font-black font-cinzel text-amber-300 tracking-wide">
                  Mode Administrateur
                </span>
                <p className="text-xs text-stone-300 font-sans leading-relaxed">
                  Rendez-vous dans le <strong className="text-amber-400 font-bold">Dictionnaire</strong> pour ajouter et éditer des mots, et enregistrer des voix réelles !
                </p>
              </div>

              {/* Dismiss Pill */}
              <div className="mt-1 px-3 py-1 rounded-full bg-white/10 text-[10px] sm:text-xs text-stone-300 font-mono flex items-center gap-1.5 border border-white/15">
                <span>Touchez pour continuer</span>
                <span className="text-amber-400 font-bold">✓</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overlay Niveau 1 Mode Aventure : Consultation du Dictionnaire Amawal */}
      <AnimatePresence>
        {showLevel1AmawalOverlay && currentLevelIdx === 0 && (gameState === 'RUN' || gameState === 'NPC_DIALOGUE' || gameState === 'REGION_INTRO') && (
          <motion.div
            key="level1-amawal-coaching-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[99990] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm select-none"
            onClick={dismissLevel1AmawalOverlay}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.86, y: 14 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.88, y: -8 }}
              transition={{ type: 'spring', stiffness: 380, damping: 26 }}
              className="bg-gradient-to-b from-stone-900/98 to-stone-950/98 backdrop-blur-xl text-white border-2 border-amber-500/70 shadow-[0_0_40px_rgba(245,158,11,0.25)] rounded-2xl p-5 sm:p-6 flex flex-col items-center justify-center gap-3.5 max-w-[320px] sm:max-w-[360px] text-center relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Badge supérieur décoratif berbère */}
              <div className="flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-[10px] font-mono uppercase tracking-widest text-amber-300 font-bold">
                <Sparkles className="w-3 h-3 text-amber-400" />
                <span>Niveau 1 • Astuce d'Aventure</span>
              </div>

              {/* Animated Schematic Visual: Dictionnaire Amawal & Aura */}
              <div className="relative w-16 h-16 sm:w-18 sm:h-18 flex items-center justify-center my-1 select-none">
                {/* Halo pulsant doré */}
                <motion.div
                  animate={{ scale: [1, 1.28, 1], opacity: [0.35, 0.8, 0.35] }}
                  transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
                  className="absolute inset-0 bg-amber-500/30 rounded-full blur-lg"
                />

                {/* Icône de livre Amawal */}
                <motion.div
                  animate={{ y: [-2, 2, -2], rotate: [-2, 2, -2] }}
                  transition={{ repeat: Infinity, duration: 2.4, ease: 'easeInOut' }}
                  className="relative z-10 p-3.5 bg-gradient-to-br from-amber-500 via-amber-600 to-amber-700 rounded-2xl shadow-xl border border-amber-300/80 flex items-center justify-center text-white"
                >
                  <BookOpen className="w-8 h-8 text-white drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]" />
                </motion.div>

                {/* Doigt / Pointeur interactif pointant vers le bas */}
                <motion.div
                  animate={{ y: [0, 8, 0] }}
                  transition={{ repeat: Infinity, duration: 1.2, ease: 'easeInOut' }}
                  className="absolute -bottom-3 text-lg drop-shadow-md z-20 pointer-events-none"
                >
                  👇
                </motion.div>
              </div>

              {/* Contenu textuel explicatif */}
              <div className="flex flex-col gap-1.5 text-center">
                <h3 className="text-base sm:text-lg font-black font-cinzel text-amber-300 tracking-wide drop-shadow-sm flex items-center justify-center gap-1.5">
                  <span>Besoin d'aide ?</span>
                </h3>
                <p className="text-xs sm:text-sm text-stone-200 font-sans leading-relaxed">
                  Tu peux consulter le dictionnaire à tout moment en cliquant sur <strong className="text-amber-300 font-bold bg-amber-950/60 px-1.5 py-0.5 rounded border border-amber-500/40 whitespace-nowrap">📖 Amawal</strong> au milieu de l'écran pour retrouver le vocabulaire et t'entraîner !
                </p>
              </div>

              {/* Actions : Consulter maintenant OU Fermer pour jouer */}
              <div className="flex flex-col sm:flex-row gap-2 w-full pt-1">
                <button
                  type="button"
                  onClick={() => {
                    dismissLevel1AmawalOverlay();
                    openDictionary();
                  }}
                  className="w-full sm:flex-1 py-2 sm:py-2.5 px-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-400 hover:to-yellow-400 active:scale-95 text-stone-950 font-bold font-cinzel text-xs uppercase tracking-wider rounded-xl shadow-lg border border-amber-300 flex items-center justify-center gap-1.5 cursor-pointer transition-all"
                >
                  <span>📖</span>
                  <span>Ouvrir Amawal</span>
                </button>
                <button
                  type="button"
                  onClick={dismissLevel1AmawalOverlay}
                  className="w-full sm:flex-1 py-2 sm:py-2.5 px-3 bg-stone-800/80 hover:bg-stone-700 active:scale-95 text-stone-200 hover:text-white font-bold text-xs uppercase tracking-wider rounded-xl border border-stone-600 flex items-center justify-center gap-1 cursor-pointer transition-all"
                >
                  <span>J'ai compris</span>
                  <span className="text-amber-400 font-bold">✓</span>
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Grand Overlay au début du jeu en mode normal : Message du Créateur Yoann Baron & Contact */}
      <CreatorWelcomeModal
        isOpen={showCreatorWelcomeModal}
        onClose={dismissCreatorWelcomeModal}
        isFirstLaunch={isFirstLaunchFlow}
        onOpenLegal={() => setShowLegalRightsModal(true)}
      />

      {/* Tutoriel & Guide d'Apprentissage (Enfants/Ados & Adultes / Personnalisation) */}
      <TrainingGuideModal
        isOpen={showTrainingGuideModal}
        onClose={dismissTrainingGuideModal}
        onOpenSMS={() => {
          dismissTrainingGuideModal();
          openInventory('sms');
        }}
        onOpenAmawal={() => {
          dismissTrainingGuideModal();
          openDictionary();
        }}
      />

      {/* Modal Droits Réservés & Propriété Intellectuelle */}
      <LegalRightsModal
        isOpen={showLegalRightsModal}
        onClose={() => setShowLegalRightsModal(false)}
      />

      {/* Modal Déverrouillage Mot de Passe Mode Administrateur */}
      <AdminPasswordModal
        isOpen={showAdminPasswordModal}
        onClose={() => setShowAdminPasswordModal(false)}
        onSuccess={handleAdminAuthSuccess}
      />
      </div>
    </div>
  );
}
